/**
 * Survey Session component for respondents taking surveys
 * Handles one-question-per-screen conversational flow
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, Check, MessageSquare, Mic, Keyboard, AlertCircle } from 'lucide-react';
import Button from '../../components/Button';
import SurveyWelcomeScreen from './SurveyWelcomeScreen';
import ConversationalSurveyFlow from './ConversationalSurveyFlow';
import SurveyConsentScreen from '../../components/survey/SurveyConsentScreen';
import { SurveyTermination } from '../../components/survey/SurveyTermination';
import { SurveyAccessGate } from '../../components/survey/SurveyAccessGate';
import { TurnstileChallenge } from '../../components/survey/TurnstileChallenge';
import { collectClientSignals } from '../../utils/botDetection';
import ImageHotspotResponse from '../../components/survey/ImageHotspotResponse';
import InfoScreenDisplay from '../../components/survey/InfoScreenDisplay';
import PhotoUploadQuestion from '../../components/survey/PhotoUploadQuestion';
import QuestionText from '../../components/common/QuestionText';
import { getPublicSurvey, startSurveySession, submitResponse, submitFollowupResponse, completeSurveySession, analyzeResponseForFollowup, shareIdToSurveyId, sendSessionHeartbeat, getSessionById } from '../../utils/surveyApi';
import { computeSurveyProgress, type SessionResponse } from './surveyProgress';
import { UI_TEXT, getRandomAnalyzingMessage } from '../../config/ui-text';
import { useDelayedFlag } from './useDelayedFlag';
import { useActiveQuestionTime } from './useActiveQuestionTime';

// How long a submit must be in flight before the "thinking…" overlay appears (fast submits skip it).
const THINKING_DELAY_MS = 450;
import { ASYNC_ANALYSIS_CONFIG, ANALYSIS_TASK_STATES, VOICE_CAPTURE } from '../../config/constants';
import { computeAudioLevels, isBlobLongEnough, isReadyChunk } from '../../utils/audioCapture';
import { generateAndPrepareSetsForUser, evaluateMaxDiffDesign } from './maxDiffUtils';
import { isPhotoUploadResponseValid } from './photoUploadValidation';
import { makeAnswerSlotKey } from './answerSlot';
import { isValidAnswerFor, describeAnswerMismatch } from './answerTypes';
import { useStimulusReady, isVideoUrl, STIMULUS_LOAD_TIMEOUT_MS } from './useStimulusReady';
import { appendTranscript } from '../../utils/transcriptAppend';

const API_URL = import.meta.env.VITE_API_URL || '';

// Helper to build full URL for media (handles both uploaded files and external URLs)
const getFullMediaUrl = (url: string): string => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api/')) return `${API_URL}${url}`;
  return url;
};

/**
 * Option-media image styling. Images render at their natural aspect ratio (object-contain,
 * never cropped) inside a self-sizing container capped to an adequate size — not a forced
 * aspect-ratio box that would crop the artwork.
 */
const OPTION_MEDIA_CONTAINER_CLASS =
  'relative rounded-lg overflow-hidden border border-gray-200 bg-gray-100 flex items-center justify-center min-h-[8rem]';
const OPTION_MEDIA_IMG_CLASS = 'w-full h-auto max-h-64 object-contain';

/** Backward-compat check: true if question should randomize media order. */
function shouldRandomizeMedia(q: any): boolean {
  if (q?.randomize_media_order != null) return q.randomize_media_order;
  return q?.image_position_mode === 'randomized';
}

/**
 * Build the conjoint profile set (balanced orthogonal design) for a question's attributes.
 * Shared by the render path (which shows the profiles) and the submit path (which recovers the
 * chosen profile's `profile_details`), so the stored details always match what the respondent saw.
 */
interface ConjointAttribute {
  name: string;
  levels: string[];
}
interface ConjointProfile {
  id: string;
  attributes: Record<string, string>;
}
function generateConjointProfiles(attributes: ConjointAttribute[]): ConjointProfile[] {
  const totalLevels = attributes.reduce((sum, attr) => sum + attr.levels.length, 0);
  const minProfilesNeeded = Math.max(
    totalLevels,
    attributes.length * 2,
    12
  );

  const profiles: ConjointProfile[] = [];
  for (let i = 0; i < minProfilesNeeded; i++) {
    const profile: Record<string, string> = {};
    attributes.forEach((attr, attrIndex) => {
      const rotationMultiplier = attrIndex + 1;
      const levelIndex = Math.floor(i / rotationMultiplier) % attr.levels.length;
      profile[attr.name] = attr.levels[levelIndex];
    });
    profiles.push({ id: `profile_${i + 1}`, attributes: profile });
  }

  // Balance attribute levels across profiles for statistical validity.
  const balanced = [...profiles];
  attributes.forEach(attr => {
    const levelCounts: Record<string, number> = {};
    attr.levels.forEach((level) => { levelCounts[level] = 0; });
    balanced.forEach(profile => { levelCounts[profile.attributes[attr.name]]++; });

    const minCount = Math.min(...Object.values(levelCounts));
    const maxCount = Math.max(...Object.values(levelCounts));

    if (maxCount - minCount > 1) {
      const underrepresented = attr.levels.filter((level) => levelCounts[level] === minCount);
      let swapIndex = 0;
      for (let i = 0; i < balanced.length && underrepresented.length > 0; i++) {
        const currentLevel = balanced[i].attributes[attr.name];
        if (levelCounts[currentLevel] > minCount + 1) {
          balanced[i].attributes[attr.name] = underrepresented[swapIndex];
          levelCounts[currentLevel]--;
          levelCounts[underrepresented[swapIndex]]++;
          swapIndex = (swapIndex + 1) % underrepresented.length;
        }
      }
    }
  });
  return balanced;
}

interface AiInference {
  inferred_answer?: any;
  covered_by?: string[];   // source question id(s)
  citation?: string;       // verbatim quote from an earlier answer
  confidence?: number;     // >0.95 gate enforced server-side
  reason?: string;
}

interface QuestionResponse {
  question_id: string;
  question_type: string;
  response_data: any;
  state: string;           // includes 'ai_skipped'
  created_at: string;
  ai_inference?: AiInference;
}

interface SessionData {
  id: number;
  state: string;
  current_question_index: number;
  survey_id: number;
  responses: QuestionResponse[];
  responses_count: number;
  question_order?: number[] | null;  // Randomized question order indices
  // Adaptive-scoping narrowings the backend has staged for upcoming questions, keyed by question
  // id. Persisted on the session so the adapted text survives a resume / page reload.
  pending_adaptations?: Record<string, { adapted_question?: string }> | null;
}

const SurveySession: React.FC = () => {
  const { shareId } = useParams<{ shareId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const isPreviewMode = searchParams.get('preview') === 'true';
  const startFromParam = searchParams.get('startFrom');

  const [survey, setSurvey] = useState<any>(null);
  const [session, setSession] = useState<SessionData | null>(null);
  const previewResponsesRef = useRef<QuestionResponse[]>([]);
  const [showWelcomeScreen, setShowWelcomeScreen] = useState(!startFromParam);
  const [showConsentScreen, setShowConsentScreen] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(startFromParam ? parseInt(startFromParam) || 0 : 0);
  const [currentResponse, setCurrentResponse] = useState<any>('');
  // Adaptive question scoping: per-question_id overrides returned by submit-response when an
  // upcoming open-text question was narrowed to its still-uncovered rubric points. Keyed by id so
  // it survives advancing to that question (no reset needed); falls back to the authored text.
  const [adaptedQuestions, setAdaptedQuestions] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Synchronous re-entrancy latch. isSubmitting is state, so two taps in the same tick both read
  // the old `false` and both submit — each advancing the index off the same stale closure, which
  // skips a question. A ref flips immediately.
  const submitInFlightRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [showTextInput, setShowTextInput] = useState(false);
  // Sticky for the rest of the session once transcription fails. Voice is not offered again —
  // re-offering a service we know is down just loses more answers.
  const [transcriptionUnavailable, setTranscriptionUnavailable] = useState(false);
  const [followupQuestion, setFollowupQuestion] = useState<string | null>(null);
  // Count of follow-ups the respondent has answered this session — feeds the progress estimate
  // (each follow-up is an extra question not present in the authored array).
  const [followupsAnswered, setFollowupsAnswered] = useState(0);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  // Brief "checking your answers" state during the synchronous AI redundancy look-ahead.
  const [isCheckingSkip, setIsCheckingSkip] = useState(false);
  // "Thinking…" overlay message while a submit is in flight and the backend is doing its adaptive
  // work (scoping the next question / coverage look-ahead). The delayed flag itself comes from
  // useDelayedFlag so quick submits never flash it.
  const [thinkingMessage, setThinkingMessage] = useState('');
  const [analysisProgress, setAnalysisProgress] = useState<string>('');
  const [originalResponse, setOriginalResponse] = useState<any>(null);
  const [questionStartTime, setQuestionStartTime] = useState<string>('');
  // True active-time tracking for the current question (pauses on tab-hidden / blur / idle).
  // Mirrors the heartbeat's preview guard: never tracks in preview mode.
  const {
    getTimings: getActiveTimings,
    collectDelta: collectActiveTimeDelta,
    resetForQuestion: resetActiveTime,
  } = useActiveQuestionTime(!isPreviewMode);
  const [currentMaxDiffSetIndex, setCurrentMaxDiffSetIndex] = useState(0);
  
  // Track option media loading for choice questions
  const [optionMediaLoaded, setOptionMediaLoaded] = useState<Record<string, boolean>>({});
  const [totalOptionMedia, setTotalOptionMedia] = useState<number>(0);
  // Fail-open backstop for option media, mirroring useStimulusReady: an option image that never
  // resolves must not leave the choices disabled forever.
  const [optionMediaTimedOut, setOptionMediaTimedOut] = useState<boolean>(false);
  // Derived, not a counter: marking is keyed by mediaKey and therefore idempotent. A bare
  // `count + 1` per onLoad could be double-fired for one image and tip the gate open while a
  // different image was still in flight.
  const loadedOptionMediaCount = Object.keys(optionMediaLoaded).length;
  // Per-image paint tracking for question-level media (info-screen-style grid). Keyed by
  // question id + url so a new question starts fully unloaded — this is what lets us hide the
  // previous question's images behind a loading skeleton instead of flashing them until the
  // new src decodes into the reused <img> node.
  const [questionMediaLoaded, setQuestionMediaLoaded] = useState<Record<string, boolean>>({});
  const markQuestionMediaLoaded = useCallback((key: string) => {
    setQuestionMediaLoaded(prev => (prev[key] ? prev : { ...prev, [key]: true }));
  }, []);
  // Media that failed to load. Without this a 404 stimulus just shows the browser's broken
  // -image glyph with no explanation; we render a readable fallback instead.
  const [questionMediaErrored, setQuestionMediaErrored] = useState<Record<string, boolean>>({});
  const markQuestionMediaErrored = useCallback((key: string) => {
    setQuestionMediaErrored(prev => (prev[key] ? prev : { ...prev, [key]: true }));
  }, []);
  const [showMaxDiffInstructions, setShowMaxDiffInstructions] = useState(false);
  const [currentConjointScenarioIndex, setCurrentConjointScenarioIndex] = useState(0);
  const [maxDiffResponses, setMaxDiffResponses] = useState<Record<number, {most: string, least: string}>>({});
  const [conjointResponses, setConjointResponses] = useState<Record<number, string>>({});
  const [isTerminated, setIsTerminated] = useState(false);
  const [terminationReason, setTerminationReason] = useState<string | null>(null);
  const [terminationRedirectUrl, setTerminationRedirectUrl] = useState<string | null>(null);
  // Respondent fraud gate: non-US soft gate (retryable) and borderline-bot challenge.
  const [geoGateMessage, setGeoGateMessage] = useState<string | null>(null);
  const [showChallenge, setShowChallenge] = useState(false);
  const [isRetryingAccess, setIsRetryingAccess] = useState(false);
  const sessionStartCtxRef = useRef<{ surveyId: number; trackingId?: string } | null>(null);
  const [pendingSkipTarget, setPendingSkipTarget] = useState<number | null>(null);
  const [isSkippingConditional, setIsSkippingConditional] = useState(false);
  // Brief acknowledgement when the AI auto-skipped questions the respondent already answered.
  const [aiSkipNotice, setAiSkipNotice] = useState<{ count: number } | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const [audioLevels, setAudioLevels] = useState<number[]>(new Array(7).fill(0));
  // Mic readiness: don't tell the respondent to speak until audio is actually flowing.
  const [micStatus, setMicStatus] = useState<'idle' | 'warming' | 'listening'>('idle');
  const readinessTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const readinessFailedRef = useRef(false);
  const freqBufferRef = useRef<Uint8Array | null>(null);
  // startRecording is a stable ([]) callback, but its onstop must call the LATEST
  // transcribeAudio (which closes over session/currentQuestion for the S3 tags).
  // Route the call through a ref so we get the current binding without rebuilding
  // startRecording or fighting the declaration order.
  const transcribeAudioRef = useRef<((blob: Blob, mimeType: string, slot: string) => Promise<void>) | null>(null);

  // Memoize analyzing message to prevent flickering when component re-renders during analysis
  const analyzingMessage = useMemo(() => {
    return isAnalyzing ? getRandomAnalyzingMessage() : '';
  }, [isAnalyzing]);

  // Surface a "thinking…" overlay once a submit has been in flight long enough that the wait would
  // otherwise read as lag (the backend is scoping the next question / running coverage). The delay
  // keeps fast submits instant; the message is picked once it turns on so it doesn't flicker.
  const showThinking = useDelayedFlag(isSubmitting, THINKING_DELAY_MS);
  // Pick the message when the submit STARTS (not when the overlay flips on) so it's already in
  // state by the time the delay elapses — otherwise the heading paints empty for one frame.
  useEffect(() => {
    if (isSubmitting) setThinkingMessage(getRandomAnalyzingMessage());
  }, [isSubmitting]);

  // Function to analyze audio and update visual levels
  const analyzeAudio = useCallback(() => {
    if (!analyserRef.current) return;
    const analyser = analyserRef.current;

    // Allocate the frequency buffer once, not every frame (cheaper on slow devices).
    if (!freqBufferRef.current || freqBufferRef.current.length !== analyser.frequencyBinCount) {
      freqBufferRef.current = new Uint8Array(analyser.frequencyBinCount);
    }
    const dataArray = freqBufferRef.current;
    analyser.getByteFrequencyData(dataArray);

    setAudioLevels(computeAudioLevels(dataArray, 7));
    animationFrameRef.current = requestAnimationFrame(analyzeAudio);
  }, []);

  // Clean up audio analysis on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (readinessTimerRef.current) {
        clearTimeout(readinessTimerRef.current);
      }
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
    };
  }, []);

  // Session persistence helpers - prevent losing progress on page reload
  const getStorageKey = useCallback((surveyId: number) => `survey_session_${surveyId}`, []);

  const saveSessionToStorage = useCallback((surveyId: number, sessionId: number) => {
    try {
      localStorage.setItem(getStorageKey(surveyId), JSON.stringify({
        sessionId,
        savedAt: Date.now()
      }));
      console.log(`[SessionPersist] Saved session ${sessionId} for survey ${surveyId}`);
    } catch (e) {
      console.warn('[SessionPersist] Failed to save session to localStorage:', e);
    }
  }, [getStorageKey]);

  const getSavedSession = useCallback((surveyId: number): { sessionId: number; savedAt: number } | null => {
    try {
      const saved = localStorage.getItem(getStorageKey(surveyId));
      if (saved) {
        const parsed = JSON.parse(saved);
        // Only use sessions saved within the last 24 hours
        if (parsed.sessionId && parsed.savedAt && (Date.now() - parsed.savedAt) < 24 * 60 * 60 * 1000) {
          return parsed;
        }
      }
    } catch (e) {
      console.warn('[SessionPersist] Failed to read session from localStorage:', e);
    }
    return null;
  }, [getStorageKey]);

  const clearSavedSession = useCallback((surveyId: number) => {
    try {
      localStorage.removeItem(getStorageKey(surveyId));
      console.log(`[SessionPersist] Cleared saved session for survey ${surveyId}`);
    } catch (e) {
      console.warn('[SessionPersist] Failed to clear session from localStorage:', e);
    }
  }, [getStorageKey]);

  // Function to randomize choices while excluding the "Other" option if it exists
  const randomizeChoices = useCallback((choices: string[], hasOtherOption: boolean = false): string[] => {
    if (!choices || choices.length <= 1) return choices;

    // If there's an "Other" option and it should be excluded from randomization
    const choicesToRandomize = hasOtherOption ? choices.slice(0, -1) : choices;
    const otherOption = hasOtherOption && choices.length > 0 ? choices[choices.length - 1] : null;

    // Fisher-Yates shuffle algorithm for randomization
    const randomized = [...choicesToRandomize];
    for (let i = randomized.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [randomized[i], randomized[j]] = [randomized[j], randomized[i]];
    }

    // Add back the "Other" option at the end if it exists
    return otherOption ? [...randomized, otherOption] : randomized;
  }, []);

  // Function to calculate MaxDiff scores and get top and bottom claims
  const calculateMaxDiffRankedClaims = useCallback((
    responses: Record<number, {most?: string, least?: string, most_appealing?: string, least_appealing?: string}>,
    count: number = 3
  ): { topClaims: string[], bottomClaims: string[] } => {
    // Count most and least selections
    const mostCounts: Record<string, number> = {};
    const leastCounts: Record<string, number> = {};

    // Iterate through all set responses
    Object.values(responses).forEach(response => {
      // Handle both 'most'/'least' and 'most_appealing'/'least_appealing' keys
      const most = response.most_appealing || response.most;
      const least = response.least_appealing || response.least;

      if (most) {
        mostCounts[most] = (mostCounts[most] || 0) + 1;
      }
      if (least) {
        leastCounts[least] = (leastCounts[least] || 0) + 1;
      }
    });

    // Calculate net scores (most = +1, least = -1)
    const allItems = new Set([...Object.keys(mostCounts), ...Object.keys(leastCounts)]);
    const netScores: Record<string, number> = {};

    allItems.forEach(item => {
      const mostScore = (mostCounts[item] || 0) * 1;  // +1 per "most" selection
      const leastScore = (leastCounts[item] || 0) * -1;  // -1 per "least" selection
      netScores[item] = mostScore + leastScore;
    });

    // Sort by score (descending), then alphabetically for ties
    const sortedItems = Object.entries(netScores)
      .sort(([itemA, scoreA], [itemB, scoreB]) => {
        if (scoreB !== scoreA) return scoreB - scoreA;  // Higher score first
        return itemA.localeCompare(itemB);  // Alphabetical for ties
      });

    // Top N = highest scores, Bottom N = lowest scores
    const topClaims = sortedItems.slice(0, count).map(([item]) => item);
    const bottomClaims = sortedItems.slice(-count).reverse().map(([item]) => item);

    return { topClaims, bottomClaims };
  }, []);

  // Get the actual question index using question_order if available
  // This maps display position (currentQuestionIndex) to actual question index in survey.questions
  const getActualQuestionIndex = useCallback((displayIndex: number): number => {
    if (session?.question_order && session.question_order.length > displayIndex) {
      return session.question_order[displayIndex];
    }
    return displayIndex;
  }, [session?.question_order]);

  // Memoized current question that respects question_order
  const currentQuestion = useMemo(() => {
    if (!survey || !survey.questions || currentQuestionIndex >= survey.questions.length) {
      return null;
    }
    const actualIndex = getActualQuestionIndex(currentQuestionIndex);
    return survey.questions[actualIndex];
  }, [survey, currentQuestionIndex, getActualQuestionIndex]);

  const answerSlotKey = useMemo(
    () => makeAnswerSlotKey(
      currentQuestion?.id, currentQuestionIndex, Boolean(followupQuestion),
      followupQuestion ? followupsAnswered : (currentMaxDiffSetIndex + currentConjointScenarioIndex)
    ),
    [currentQuestion, currentQuestionIndex, followupQuestion, followupsAnswered,
     currentMaxDiffSetIndex, currentConjointScenarioIndex]
  );

  // Mirrors answerSlotKey for the async transcription callback, which resolves seconds later and
  // must not read a stale closure. Transcription is dispatched with the slot it was recorded for
  // and compared against this at write time.
  const answerSlotRef = useRef(answerSlotKey);
  useEffect(() => {
    answerSlotRef.current = answerSlotKey;
  }, [answerSlotKey]);

  // Progress as a PERCENTAGE of the questions this respondent will actually face (estimated from
  // conditional logic — longest branch until decided, then narrowed), not the raw array length. A
  // 92-question survey that branches to ~37 should never read "5 / 92".
  const progress = useMemo(() => computeSurveyProgress({
    questions: survey?.questions ?? [],
    blocks: survey?.blocks ?? null,
    responses: (session?.responses ?? []) as unknown as SessionResponse[],
    followupsAnswered,
    followupActive: !!followupQuestion,
  }), [survey, session, followupsAnswered, followupQuestion]);

  // Tracks whether the CURRENT showTextInput value was forced by a question's author (via a
  // disable_* flag) rather than chosen by the respondent. Lets us clear an author-forced channel
  // when the next question has no restriction, without clobbering a respondent's own toggle.
  const inputChannelAuthorForcedRef = useRef(false);

  // Force the input channel when the author disabled one for this question. When neither flag is
  // set, return to the default IF the prior value was author-forced; otherwise leave it as-is so a
  // respondent's own text/voice toggle carries over (preserving their prior choice).
  useEffect(() => {
    if (!currentQuestion) return;
    // Overrides the author's channel choice, including disable_text_input: if transcription is
    // down, typing is the only way left to answer, so blocking it would strand the respondent.
    if (transcriptionUnavailable) {
      setShowTextInput(true);
      return;
    }
    if (currentQuestion.disable_voice_input) {
      setShowTextInput(true); // voice disabled -> force text
      inputChannelAuthorForcedRef.current = true;
    } else if (currentQuestion.disable_text_input) {
      setShowTextInput(false); // text disabled -> force voice
      inputChannelAuthorForcedRef.current = true;
    } else if (inputChannelAuthorForcedRef.current) {
      // Previous question author-forced a channel; this one imposes no restriction, so revert to
      // the voice-first default instead of carrying the forced value over.
      setShowTextInput(false);
      inputChannelAuthorForcedRef.current = false;
    }
    // else: no restriction now and none carried over -> keep the respondent's current choice.
  }, [currentQuestion, transcriptionUnavailable]);

  // Memoized randomized choices for the current question to prevent re-shuffling on re-renders
  const randomizedChoices = useMemo(() => {
    if (!currentQuestion) {
      return [];
    }

    const originalChoices = currentQuestion.choices || [];
    const shouldRandomize = currentQuestion.randomize_choices;
    const hasOther = currentQuestion.allow_other;

    if (!shouldRandomize || originalChoices.length <= 1) {
      return originalChoices;
    }

    return randomizeChoices(originalChoices, hasOther);
  }, [currentQuestion, randomizeChoices]);

  /** Question-level media_urls in display order; originalIndices[i] is the storage index for slot i (for choice-image mode). */
  const questionMediaDisplay = useMemo(() => {
    if (!currentQuestion?.media_urls?.length) {
      return { orderedUrls: [] as string[], originalIndices: [] as number[] };
    }
    const urls = currentQuestion.media_urls;
    const n = urls.length;
    if (n <= 1) {
      return { orderedUrls: [...urls], originalIndices: n === 1 ? [0] : [] };
    }
    const identity = Array.from({ length: n }, (_, i) => i);
    if (shouldRandomizeMedia(currentQuestion)) {
      const pairs: { url: string; orig: number }[] = urls.map((url: string, orig: number) => ({ url, orig }));
      for (let i = n - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [pairs[i], pairs[j]] = [pairs[j], pairs[i]];
      }
      return {
        orderedUrls: pairs.map((p) => p.url),
        originalIndices: pairs.map((p) => p.orig),
      };
    }
    return { orderedUrls: [...urls], originalIndices: identity };
  }, [
    currentQuestion?.id,
    JSON.stringify(currentQuestion?.media_urls),
    currentQuestion?.randomize_media_order,
    currentQuestion?.image_position_mode,
  ]);

  const orderedQuestionMediaUrls = questionMediaDisplay.orderedUrls;
  const questionMediaOriginalIndices = questionMediaDisplay.originalIndices;

  // Don't let a respondent answer a stimulus they haven't seen: choice buttons submit on click,
  // so while the concept image is still in flight a click records a rating of packaging that
  // never rendered. Fails open (see useStimulusReady).
  const { ready: stimulusReady, markResolved: markStimulusResolved } = useStimulusReady(
    currentQuestion?.id,
    orderedQuestionMediaUrls,
  );

  // Memoized randomized matrix rows for matrix questions
  const randomizedMatrixRows = useMemo(() => {
    if (!currentQuestion || currentQuestion.type !== 'matrix_rating') {
      return [];
    }

    const originalRows = currentQuestion.matrix_rows || currentQuestion.choices || [];
    const shouldRandomize = currentQuestion.randomize_choices;

    if (!shouldRandomize || originalRows.length <= 1) {
      return originalRows;
    }

    // For matrix questions, randomize rows without excluding any items (no "Other" option)
    return randomizeChoices(originalRows, false);
  }, [currentQuestion, randomizeChoices]);

  // Memoized MaxDiff sets for the current question
  const maxDiffSets = useMemo(() => {
    if (!currentQuestion || currentQuestion.type !== 'max_diff' || !currentQuestion.choices || !session) {
      return [];
    }

    // Use session ID as unique identifier for reproducible randomization per user
    const sets = generateAndPrepareSetsForUser(currentQuestion.choices, session.id.toString(), 4, 4);

    // Evaluate and log the design quality for this session
    const evaluation = evaluateMaxDiffDesign(sets, currentQuestion.choices);
    console.log(`[MaxDiff Evaluation - Session ${session.id}]`, evaluation);

    return sets;
  }, [currentQuestion, session]);

  // Reset state when question changes
  // Fold any server-staged adaptive narrowings into the display map whenever the session loads or
  // updates. The submit response delivers the next question's adaptation inline, but a resume /
  // page reload only carries it on session.pending_adaptations — without this, a respondent who
  // reloads on an adapted question would see the ORIGINAL authored text.
  useEffect(() => {
    const pending = session?.pending_adaptations;
    if (!pending || typeof pending !== 'object') return;
    setAdaptedQuestions(prev => {
      let changed = false;
      const next = { ...prev };
      for (const [qid, ad] of Object.entries(pending)) {
        const text = ad?.adapted_question;
        if (text && next[qid] !== text) { next[qid] = text; changed = true; }
      }
      return changed ? next : prev;
    });
  }, [session]);

  useEffect(() => {
    setCurrentMaxDiffSetIndex(0);
    setCurrentConjointScenarioIndex(0);
    setMaxDiffResponses({});
    setConjointResponses({});
    setCurrentResponse('');
    setFollowupQuestion(null);
    setQuestionStartTime(new Date().toISOString());
    resetActiveTime();

    // Reset option media loading state
    setOptionMediaLoaded({});
    setOptionMediaTimedOut(false);
    // Reset question-level media paint state so the new question shows its loading skeleton
    // instead of the previous question's images.
    setQuestionMediaLoaded({});
    setQuestionMediaErrored({});
    
    // Calculate total option media for current question
    if (currentQuestion?.option_media) {
      const optionMediaValues = Object.values(currentQuestion.option_media) as string[][];
      const total = optionMediaValues.reduce(
        (sum, urls) => sum + (urls?.length || 0), 
        0
      );
      setTotalOptionMedia(total);
    } else {
      setTotalOptionMedia(0);
    }
  }, [currentQuestionIndex, currentQuestion, resetActiveTime]);

  // Marks one option image resolved (loaded OR errored — an image that 404s is resolved, it just
  // isn't visible). Idempotent, so a repeated onLoad cannot inflate the count.
  const markOptionMediaLoaded = useCallback((mediaKey: string) => {
    setOptionMediaLoaded(prev => (prev[mediaKey] ? prev : { ...prev, [mediaKey]: true }));
  }, []);

  // Fail open: if the option images never resolve (cached image that fired onLoad before React
  // attached it, dead CDN, ...), unlock rather than strand the respondent on a dead question.
  useEffect(() => {
    setOptionMediaTimedOut(false);
    if (totalOptionMedia === 0) return;
    const timer = setTimeout(() => setOptionMediaTimedOut(true), STIMULUS_LOAD_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [currentQuestion?.id, totalOptionMedia]);

  // Helper function to evaluate percentage allocation conditions
  const evaluatePercentageCondition = useCallback((responseData: any[], condition: any): boolean => {
    // Extract the dict value from original_response
    const originalResponse = responseData?.find((item: any) => item.type === 'original_response');
    if (!originalResponse?.value || typeof originalResponse.value !== 'object') return true;

    const allocations = originalResponse.value as Record<string, number>;
    const optionValue = allocations[condition.option_key];

    if (optionValue === undefined) return true; // Option not found, show by default

    const threshold = condition.threshold ?? 0;

    switch (condition.operator) {
      case '>':  return optionValue > threshold;
      case '>=': return optionValue >= threshold;
      case '=':  return optionValue === threshold;
      case '<':  return optionValue < threshold;
      case '<=': return optionValue <= threshold;
      default:   return true;
    }
  }, []);

  // Helper function to evaluate choice-based conditions
  const evaluateChoiceCondition = useCallback((responseData: any[], condition: any): boolean => {
    const extractSelectedValues = (data: any[]): string[] => {
      if (!data || !Array.isArray(data)) return [];
      const values: string[] = [];
      // Handle all choice-based question types that can be used as display condition sources
      const choiceTypes = ['single_choice', 'multiple_choice', 'yes_no', 'dropdown', 'original_response'];
      for (const item of data) {
        if (choiceTypes.includes(item.type)) {
          values.push(String(item.value));
        }
      }
      return values;
    };

    const selectedValues = extractSelectedValues(responseData);

    if (condition.operator === 'selected') {
      return selectedValues.includes(condition.value);
    } else if (condition.operator === 'not_selected') {
      return !selectedValues.includes(condition.value);
    }
    return true;
  }, []);

  const evaluateSingleCondition = useCallback((condition: any, responses: any[]): boolean => {
    const sourceResponse = responses?.find(
      (r: any) => r.question_id === condition.source_question_id
    );
    // A source question that was skipped (conditionally or by AI) was never genuinely
    // answered, so a downstream condition referencing it is undecided. Treat undecided as
    // "show" (never hide a question by accident — that would silently drop data), matching
    // the backend / surveyProgress 'undecided' handling instead of evaluating a stale/empty
    // skipped value.
    const SKIPPED_SOURCE_STATES = ['conditionally_skipped', 'ai_skipped', 'skipped'];
    if (sourceResponse && SKIPPED_SOURCE_STATES.includes(sourceResponse.state)) return true;
    if (!sourceResponse?.response_data) return true;

    const isPercentageCondition = ['>', '>=', '=', '<', '<='].includes(condition.operator);
    if (isPercentageCondition) {
      return evaluatePercentageCondition(sourceResponse.response_data, condition);
    } else {
      return evaluateChoiceCondition(sourceResponse.response_data, condition);
    }
  }, [evaluatePercentageCondition, evaluateChoiceCondition]);

  // Evaluate display conditions -- supports compound AND/OR conditions
  const evaluateDisplayCondition = useCallback((question: any, responses: any[]): boolean => {
    if (!question?.display_condition) return true;

    const dc = question.display_condition;

    // New compound format: { logic, conditions[] }
    if (dc.conditions && Array.isArray(dc.conditions) && dc.conditions.length > 0) {
      const logic = dc.logic || 'and';
      if (logic === 'or') {
        return dc.conditions.some((c: any) => evaluateSingleCondition(c, responses));
      }
      return dc.conditions.every((c: any) => evaluateSingleCondition(c, responses));
    }

    // Legacy flat format: { source_question_id, operator, value, ... }
    if (dc.source_question_id) {
      return evaluateSingleCondition(dc, responses);
    }

    return true;
  }, [evaluateSingleCondition]);

  // Evaluate block display condition - checks if a block should be shown based on its condition
  const evaluateBlockDisplayCondition = useCallback((blockId: string | undefined, responses: any[]): boolean => {
    if (!blockId || !survey?.blocks) return true;

    const block = survey.blocks.find((b: any) => b.id === blockId);
    if (!block?.display_condition) return true;

    const dc = block.display_condition;

    // Compound format: { logic, conditions[] }
    if (dc.conditions && Array.isArray(dc.conditions) && dc.conditions.length > 0) {
      const logic = dc.logic || 'and';
      if (logic === 'or') {
        return dc.conditions.some((c: any) => evaluateSingleCondition(c, responses));
      }
      return dc.conditions.every((c: any) => evaluateSingleCondition(c, responses));
    }

    // Legacy flat format: { source_question_id, operator, value, ... }
    // Mirror the question-level evaluator so legacy-flat block conditions are honored.
    if (dc.source_question_id) {
      return evaluateSingleCondition(dc, responses);
    }

    return true;
  }, [survey, evaluateSingleCondition]);

  // Check if current question should be skipped (memoized to prevent flashing)
  const shouldSkipCurrentQuestion = useMemo(() => {
    if (!session || !survey || !currentQuestion || showWelcomeScreen) return false;

    // Check block-level condition first
    if (currentQuestion.block_id && !evaluateBlockDisplayCondition(currentQuestion.block_id, session.responses || [])) {
      return true;
    }

    // Then check question-level condition
    if (!currentQuestion.display_condition) return false;
    return !evaluateDisplayCondition(currentQuestion, session.responses || []);
  }, [session, survey, currentQuestion, showWelcomeScreen, evaluateDisplayCondition, evaluateBlockDisplayCondition]);

  // Evaluate display conditions and skip questions that don't meet conditions
  useEffect(() => {
    const skipConditionalQuestion = async () => {
      if (!shouldSkipCurrentQuestion || isSubmitting || isSkippingConditional) return;

      // Don't re-skip a question the backend already wrote a response for (e.g. ai_skipped).
      // Backend-written responses are authoritative; acting here would double-submit.
      const alreadyHandled = (session?.responses || []).some(
        (r) => r.question_id === currentQuestion?.id
      );
      if (alreadyHandled) return;

      setIsSkippingConditional(true);

      // Preview mode: just skip locally without API calls
      if (isPreviewMode) {
        if (currentQuestionIndex + 1 < survey!.questions.length) {
          setCurrentQuestionIndex(currentQuestionIndex + 1);
        } else {
          setIsTerminated(true);
          setTerminationReason('completed');
        }
        setIsSkippingConditional(false);
        return;
      }

      try {
        const result = await submitResponse(
          session!.id,
          currentQuestion.id,
          { skipped_reason: 'conditionally_skipped' },
          currentQuestion.type,
          questionStartTime,
          true,
          null,
          getActiveTimings()
        );

        setSession(result.session_data);

        if (currentQuestionIndex + 1 < survey!.questions.length) {
          setCurrentQuestionIndex(currentQuestionIndex + 1);
        } else {
          const completeResult = await completeSurveySession(session!.id);
          clearSavedSession(session!.survey_id);
          if (completeResult.redirect_url) {
            window.location.href = completeResult.redirect_url;
          } else {
            navigate(`/survey-complete/${session!.id}`);
          }
        }
      } catch (error) {
        console.error('Failed to skip conditional question:', error);
        // Surface the failure like every other submit path so the respondent isn't left
        // stuck on a bare skip spinner with no feedback.
        setError('Failed to load the next question. Please try again.');
      } finally {
        setIsSkippingConditional(false);
      }
    };

    skipConditionalQuestion();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldSkipCurrentQuestion, currentQuestionIndex, isSubmitting, isSkippingConditional]);

  // Auto-dismiss the AI-skip acknowledgement toast after a few seconds.
  useEffect(() => {
    if (!aiSkipNotice) return;
    const t = setTimeout(() => setAiSkipNotice(null), 3000);
    return () => clearTimeout(t);
  }, [aiSkipNotice]);

  // Start (or resume) the respondent session, running the backend fraud gate.
  // Re-invokable so the geo soft-gate can retry and the bot challenge can re-submit
  // with a Turnstile token. Reads survey/tracking context captured at load time.
  const attemptStartSession = useCallback(async (turnstileToken?: string) => {
    const ctx = sessionStartCtxRef.current;
    if (!ctx) return;

    try {
      const signals = await collectClientSignals();
      const sessionResult = await startSurveySession(
        ctx.surveyId,
        ctx.trackingId,
        signals,
        turnstileToken
      );

      if (!sessionResult) {
        setError('Failed to start survey. Please try again.');
        return;
      }

      // Fraud-gate outcomes (returned before a session exists)
      if (sessionResult.status === 'blocked') {
        setShowChallenge(false);
        setGeoGateMessage(null);
        setIsTerminated(true);
        setTerminationReason('automation_blocked');
        return;
      }
      if (sessionResult.status === 'challenge_required') {
        setGeoGateMessage(null);
        // Backend requires a Turnstile challenge but no site key is configured to
        // render one — surface an error instead of looping on a token-less retry.
        if (!import.meta.env.VITE_TURNSTILE_SITE_KEY) {
          setError(UI_TEXT.AI_SURVEY.FRAUD_GATE.CHALLENGE.UNAVAILABLE);
          return;
        }
        setShowChallenge(true);
        return;
      }
      if (sessionResult.status === 'geo_blocked') {
        setShowChallenge(false);
        setGeoGateMessage(sessionResult.message || '');
        return;
      }

      // Passed the gate (or gate disabled): clear any gate UI before continuing
      setShowChallenge(false);
      setGeoGateMessage(null);

      if (sessionResult.status === 'quota_full') {
        setIsTerminated(true);
        setTerminationReason('quota_full');
        setTerminationRedirectUrl(sessionResult.redirect_url || null);
        clearSavedSession(ctx.surveyId);
        return;
      }

      if (sessionResult.status === 'already_completed') {
        setIsTerminated(true);
        setTerminationReason('completed');
        setTerminationRedirectUrl(sessionResult.redirect_url || null);
        clearSavedSession(ctx.surveyId);
        return;
      }

      if (sessionResult.status === 'screened_out') {
        setIsTerminated(true);
        setTerminationReason('screened_out');
        setTerminationRedirectUrl(sessionResult.redirect_url || null);
        clearSavedSession(ctx.surveyId);
        return;
      }

      if (!sessionResult.session_data) {
        setError('Failed to start survey. Please try again.');
        return;
      }

      // Persist session to localStorage so progress survives a page reload
      // (covers both resumed and newly created sessions).
      saveSessionToStorage(ctx.surveyId, sessionResult.session_data.id);

      // For resumed sessions, restore question index and skip welcome screen
      if (sessionResult.status === 'resumed') {
        setSession(sessionResult.session_data);
        if (sessionResult.session_data.current_question_index > 0) {
          setCurrentQuestionIndex(sessionResult.session_data.current_question_index);
          setShowWelcomeScreen(false);
        }
        return;
      }

      // New session created
      setSession(sessionResult.session_data);
    } catch (error) {
      console.error('Failed to start session:', error);
      setError('Failed to start survey. Please try again.');
    }
  }, [saveSessionToStorage, clearSavedSession]);

  // Soft-gate retry (e.g. after the respondent disables a VPN).
  const handleAccessRetry = useCallback(async () => {
    setIsRetryingAccess(true);
    try {
      await attemptStartSession();
    } finally {
      setIsRetryingAccess(false);
    }
  }, [attemptStartSession]);

  // Load survey and create session
  useEffect(() => {
    const loadSurveyAndCreateSession = async () => {
      if (!shareId || session) return;
      
      try {
        setIsLoading(true);
        setError(null);
        
        const surveyId = shareIdToSurveyId(shareId);

        // Preview mode: load survey via authenticated endpoint, no session creation
        if (isPreviewMode) {
          const { getSurvey } = await import('../../utils/surveyApi');
          const surveyResult = await getSurvey(surveyId);
          setSurvey(surveyResult.survey);
          // Create a fake local session for preview
          setSession({
            id: -1,
            state: 'in_progress',
            current_question_index: startFromParam ? parseInt(startFromParam) || 0 : 0,
            survey_id: surveyId,
            responses: [],
            responses_count: 0,
            question_order: null,
          });
          setIsLoading(false);
          return;
        }

        const surveyResult = await getPublicSurvey(surveyId);
        setSurvey(surveyResult.survey);

        // Check localStorage for an existing session (prevents losing progress on page reload)
        const savedSession = getSavedSession(surveyId);
        if (savedSession) {
          console.log(`[SessionPersist] Found saved session ${savedSession.sessionId}, attempting to resume...`);
          const existingSession = await getSessionById(savedSession.sessionId);

          if (existingSession) {
            console.log(`[SessionPersist] Successfully resumed session ${existingSession.id} at question ${existingSession.current_question_index}`);
            setSession(existingSession);

            // Restore question index if session has progressed
            if (existingSession.current_question_index > 0) {
              setCurrentQuestionIndex(existingSession.current_question_index);
              // Skip welcome screen for resumed sessions
              setShowWelcomeScreen(false);
            }
            setIsLoading(false);
            return;
          } else {
            // Saved session is no longer valid (completed, expired, or deleted)
            console.log(`[SessionPersist] Saved session ${savedSession.sessionId} is no longer valid, clearing...`);
            clearSavedSession(surveyId);
          }
        }

        // Extract tracking ID from URL query parameters using survey's configured tracking param names
        let trackingId: string | null = null;

        console.log('Survey redirect_config:', surveyResult.survey.redirect_config);
        console.log('URL search params:', Object.fromEntries(searchParams.entries()));

        if (surveyResult.survey.redirect_config?.tracking_param) {
          // Get the tracking parameter names from the survey's redirect config
          const trackingParamNames = Object.keys(surveyResult.survey.redirect_config.tracking_param);
          console.log('Configured tracking parameter names:', trackingParamNames);

          // Look for any of these parameter names in the URL
          for (const paramName of trackingParamNames) {
            const paramValue = searchParams.get(paramName);
            console.log(`Checking for param "${paramName}":`, paramValue);
            if (paramValue) {
              trackingId = paramValue;
              console.log(`✓ Found tracking ID "${trackingId}" from configured parameter "${paramName}"`);
              break;
            }
          }

          if (!trackingId) {
            console.warn('No tracking ID found in URL parameters');
          }
        } else {
          console.log('No redirect_config or tracking_param configured for this survey');
        }

        // Capture context so the session start can be retried (geo gate) or
        // re-submitted with a Turnstile token (bot challenge), then run the gate.
        sessionStartCtxRef.current = { surveyId, trackingId: trackingId || undefined };
        await attemptStartSession();

      } catch (error) {
        console.error('Failed to load survey or create session:', error);
        setError('Failed to start survey. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    loadSurveyAndCreateSession();
  }, [shareId, searchParams, attemptStartSession]);

  // Session heartbeat: ping every 60s to keep the session marked as active
  useEffect(() => {
    if (!session?.id || isPreviewMode) return;
    const intervalId = setInterval(() => {
      sendSessionHeartbeat(session.id);
    }, 60_000);

    const handleBeforeUnload = () => {
      sendSessionHeartbeat(session.id);
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [session?.id]);

  // Track question start time when question changes
  useEffect(() => {
    if (survey && session && !followupQuestion) {
      setQuestionStartTime(new Date().toISOString());
      resetActiveTime();
    }
  }, [currentQuestionIndex, survey, session, followupQuestion, resetActiveTime]);

  const handleResponseChange = useCallback((value: any) => {
    setCurrentResponse(value);
  }, []);

  const handleStartSurvey = useCallback(() => {
    setShowWelcomeScreen(false);
    setShowConsentScreen(true);
  }, []);

  const handleAcceptConsent = useCallback(() => {
    setShowConsentScreen(false);
  }, []);

  const startRecording = useCallback(async () => {
    // Pin the answer slot at the moment the respondent starts speaking. Everything downstream is
    // async, so this is the only point where "which question is this for" is unambiguous.
    const recordedForSlot = answerSlotRef.current;
    readinessFailedRef.current = false;
    try {
      // Request microphone access with enhanced audio settings for better quality
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,  // Reduce echo feedback
          noiseSuppression: true,  // Filter background noise
          sampleRate: 44100        // High quality sample rate
        }
      });

      // Check for OpenAI-supported audio formats in order of preference
      // Note: OpenAI supports .mp3, .mp4, .mpeg, .mpga, .m4a, .wav, .webm
      const mimeType = MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'              // WebM with Opus (OpenAI supported)
        : MediaRecorder.isTypeSupported('audio/mp4')
        ? 'audio/mp4'               // MP4 (OpenAI supported, Safari preferred)
        : MediaRecorder.isTypeSupported('audio/wav')
        ? 'audio/wav'               // WAV (OpenAI supported, universal)
        : '';

      if (!mimeType) {
        throw new Error('No supported audio format found');
      }

      // Set up Web Audio API for real-time audio analysis
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();

      // Configure analyser for responsive visualization
      analyser.fftSize = 512; // Higher resolution for better accuracy
      analyser.smoothingTimeConstant = 0.1; // Much less smoothing for immediate response
      analyser.minDecibels = -90;
      analyser.maxDecibels = -10;

      source.connect(analyser);

      // Store references for cleanup and animation
      audioContextRef.current = audioContext;
      analyserRef.current = analyser;

      // Start audio level analysis animation
      animationFrameRef.current = requestAnimationFrame(analyzeAudio);

      // Create MediaRecorder with optimal settings
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType,
        audioBitsPerSecond: 128000 // 128 kbps for good quality without huge files
      });

      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      // Handle audio data as it becomes available
      mediaRecorder.ondataavailable = (event) => {
        if (isReadyChunk(event.data.size)) {
          audioChunksRef.current.push(event.data);
          // First real chunk = proof capture is actually live. Only NOW is it safe
          // to tell the respondent to speak — before this the mic pipeline may not
          // be capturing yet on a slow device, silently dropping their first words.
          if (readinessTimerRef.current) {
            clearTimeout(readinessTimerRef.current);
            readinessTimerRef.current = null;
            setMicStatus('listening');
            setIsRecording(true);
            console.log('Recording confirmed live (first audio chunk received)');
          }
        }
      };

      // Handle recording completion
      mediaRecorder.onstop = async () => {
        if (readinessFailedRef.current) {
          stream.getTracks().forEach(track => track.stop());
          return;
        }

        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        console.log(`Final audio blob: ${audioBlob.size} bytes, MIME type: ${mimeType}`);

        // Validate minimum audio data size (meaningful transcription threshold)
        if (!isBlobLongEnough(audioBlob.size, VOICE_CAPTURE.MIN_BLOB_BYTES)) {
          console.warn(`Audio recording too short: ${audioBlob.size} bytes`);
          setError(UI_TEXT.AI_SURVEY.SESSION.ERRORS.RECORDING_TOO_SHORT);
          // Clean up media stream
          stream.getTracks().forEach(track => track.stop());
          return;
        }

        // Proceed with transcription for valid audio, passing the actual MIME type.
        // Via ref so we invoke the latest transcribeAudio (current session/question).
        await transcribeAudioRef.current?.(audioBlob, mimeType, recordedForSlot);
        // Always clean up the media stream to prevent hanging
        stream.getTracks().forEach(track => track.stop());
      };

      // Handle recording errors
      mediaRecorder.onerror = (event) => {
        console.error('MediaRecorder error:', event);
        if (readinessTimerRef.current) {
          clearTimeout(readinessTimerRef.current);
          readinessTimerRef.current = null;
        }
        setError(UI_TEXT.AI_SURVEY.SESSION.ERRORS.RECORDING_FAILED);
        setMicStatus('idle');
        setIsRecording(false);
        // Clean up on error
        stream.getTracks().forEach(track => track.stop());
      };

      // Show "warming up" immediately; the first audio chunk (ondataavailable) is
      // what flips us to "listening" — never a timer or MediaRecorder.state alone.
      setMicStatus('warming');
      mediaRecorder.start(VOICE_CAPTURE.TIMESLICE_MS);

      // If no audio chunk ever arrives, capture never came up (slow/blocked device):
      // stop cleanly and drop the respondent to typing rather than a dead recording.
      readinessTimerRef.current = setTimeout(() => {
        readinessTimerRef.current = null;
        readinessFailedRef.current = true; // tell onstop to skip the too-short path
        try { if (mediaRecorder.state === 'recording') mediaRecorder.stop(); } catch { /* noop */ }
        stream.getTracks().forEach(track => track.stop());
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }
        audioChunksRef.current = [];
        setMicStatus('idle');
        setIsRecording(false);
        setShowTextInput(true);
        setError(UI_TEXT.AI_SURVEY.SESSION.ERRORS.MIC_NOT_READY);
      }, VOICE_CAPTURE.READINESS_TIMEOUT_MS);

    } catch (error) {
      console.error('Error starting recording:', error);
      setError(UI_TEXT.AI_SURVEY.SESSION.ERRORS.MIC_PERMISSION);
      setMicStatus('idle');
      setIsRecording(false);
    }
  }, []);

  const stopRecording = useCallback(() => {
    // Check if MediaRecorder exists and is currently recording
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      console.log('Stopping recording...');
      // Stop the recording - this will trigger the onstop event
      mediaRecorderRef.current.stop();
      // Update UI state immediately to show recording has stopped
      setIsRecording(false);
    } else {
      console.warn('Cannot stop recording: MediaRecorder not recording');
    }

    // Stop audio analysis animation
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    // Cancel any pending readiness timeout (recording ended before it fired).
    if (readinessTimerRef.current) {
      clearTimeout(readinessTimerRef.current);
      readinessTimerRef.current = null;
    }
    setMicStatus('idle');

    // Clean up audio context
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close();
    }
    audioContextRef.current = null;
    analyserRef.current = null;

    // Reset audio levels to zero
    setAudioLevels(new Array(7).fill(0));
  }, []);

  const transcribeAudio = useCallback(async (audioBlob: Blob, mimeType: string, recordedForSlot: string) => {
    try {
      console.log(`Starting transcription for audio blob: ${audioBlob.size} bytes, MIME type: ${mimeType}`);
      setIsTranscribing(true);

      // Determine file extension based on MIME type (OpenAI supported formats only)
      const getFileExtension = (mimeType: string): string => {
        if (mimeType.includes('webm')) return '.webm';
        if (mimeType.includes('mp4')) return '.mp4';
        if (mimeType.includes('wav')) return '.wav';
        if (mimeType.includes('mp3')) return '.mp3';
        if (mimeType.includes('m4a')) return '.m4a';
        return '.webm'; // Default fallback to WebM (OpenAI supported)
      };

      const fileExtension = getFileExtension(mimeType);
      const fileName = `recording${fileExtension}`;

      // Create form data with audio file for backend transcription
      const formData = new FormData();
      formData.append('audio', audioBlob, fileName);

      if (session) {
        formData.append('context_type', 'survey');
        const trackingId = sessionStartCtxRef.current?.trackingId;
        const questionId = currentQuestion?.id;
        formData.append(
          'context_metadata',
          JSON.stringify({
            session_id: String(session.id),
            survey_id: String(session.survey_id),
            ...(questionId ? { question_id: String(questionId) } : {}),
            ...(trackingId ? { tracking_id: String(trackingId) } : {}),
          })
        );
      }

      // Send audio to backend transcription endpoint
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/transcribe`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Transcription request failed:', errorText);
        throw new Error(`Transcription failed: ${response.status}`);
      }

      const result = await response.json();
      console.log('Transcription response:', result);

      // Check if transcription was started successfully
      if (result.status === 'processing' && result.task_id) {
        console.log(`Polling for transcription result with task ID: ${result.task_id}`);
        // Poll for transcription result using the task ID
        const transcribedText = await pollTranscriptionResult(result.task_id);

        console.log(`Transcription completed: "${transcribedText}"`);
        // A transcript may resolve seconds after the respondent moved on (Next isn't gated on
        // isTranscribing). Writing it into whatever question is on screen now would put words
        // the respondent never said about it into that answer, so drop it instead.
        if (answerSlotRef.current !== recordedForSlot) {
          console.warn(
            `Discarding transcript recorded for "${recordedForSlot}" — now on "${answerSlotRef.current}"`
          );
          return;
        }

        if (!transcribedText) {
          // Empty transcript = silence or unintelligible audio. The backend reports this as
          // success with text:"" so nothing downstream notices. Drop the respondent into the
          // text box, which already renders whatever was transcribed so far, so they can see
          // what survived and type the rest. Deliberately NOT setting transcriptionUnavailable:
          // one unintelligible clip doesn't mean the service is down, and they can go back to
          // the mic. This is the failure that silently truncated 412 production answers.
          setShowTextInput(true);
          setError(UI_TEXT.AI_SURVEY.SESSION.ERRORS.TRANSCRIPTION_EMPTY);
          return;
        }

        // Append to the existing response, or set as new. appendTranscript drops a non-string
        // prior value — see its docblock for why that can't be a real answer here.
        setCurrentResponse((prevResponse: any) => {
          const newResponse = appendTranscript(prevResponse, transcribedText);
          console.log(`Updated response: "${newResponse}"`);
          return newResponse;
        });
      } else {
        throw new Error(result.error || 'Transcription failed - no task ID returned');
      }

    } catch (error) {
      console.error('Transcription error:', error);
      // Transcription is down, not just unlucky on this clip. Move the respondent to typing for
      // the REST of the session rather than telling them to retry a service that isn't working —
      // silently losing their spoken answers is how we ended up with blank stored responses.
      setTranscriptionUnavailable(true);
      setShowTextInput(true);
      setError(UI_TEXT.AI_SURVEY.SESSION.ERRORS.TRANSCRIPTION_UNAVAILABLE);
    } finally {
      // Always reset transcribing state
      setIsTranscribing(false);
    }
  }, [currentResponse, session, currentQuestion]);

  // Keep the ref pointing at the latest transcribeAudio so startRecording's onstop
  // (a stable []-dep callback) always invokes the current binding.
  useEffect(() => {
    transcribeAudioRef.current = transcribeAudio;
  }, [transcribeAudio]);

  const pollTranscriptionResult = useCallback(async (taskId: string): Promise<string> => {
    const maxAttempts = 30; // 30 seconds timeout
    const pollInterval = 1000; // 1 second intervals

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const response = await fetch(`${import.meta.env.VITE_API_URL}/api/transcribe/status/${taskId}`);

        if (!response.ok) {
          throw new Error('Failed to check transcription status');
        }

        const result = await response.json();

        if (result.status === 'completed') {
          return result.text || '';
        } else if (result.status === 'failed') {
          throw new Error(result.error || 'Transcription failed');
        }

        // Still processing, wait before next poll
        await new Promise(resolve => setTimeout(resolve, pollInterval));

      } catch (error) {
        console.error('Error polling transcription status:', error);
        throw error;
      }
    }

    throw new Error('Transcription timeout');
  }, []);

  const handlePreviewSkip = useCallback(() => {
    if (!survey?.questions) return;
    const nextIndex = currentQuestionIndex + 1;
    if (nextIndex >= survey.questions.length) {
      setIsTerminated(true);
      setTerminationReason('completed');
    } else {
      setCurrentResponse('');
      setFollowupQuestion(null);
      setCurrentQuestionIndex(nextIndex);
    }
  }, [currentQuestionIndex, survey]);

  const handleSubmitResponse = useCallback(async (responseValue?: any) => {
    if (!session || !survey || !survey.questions || currentQuestionIndex >= survey.questions.length) return;
    // The auto-submitting choice buttons are not disabled while a submit runs, so this is the
    // only thing standing between a double-tap and a silently skipped question.
    if (submitInFlightRef.current) return;
    submitInFlightRef.current = true;

    const actualQuestionIndex = session.question_order && session.question_order.length > currentQuestionIndex
      ? session.question_order[currentQuestionIndex]
      : currentQuestionIndex;
    const questionToSubmit = survey.questions[actualQuestionIndex];
    const responseToSubmit = responseValue !== undefined ? responseValue : currentResponse;

    // Last line of defence before the answer is persisted. canProceed only guards the Next
    // button, and several choice inputs auto-submit without consulting it. Refusing here is what
    // makes "a photo object can never be stored as someone's prose" true rather than likely.
    if (!followupQuestion && !isValidAnswerFor(questionToSubmit.type, responseToSubmit)) {
      console.error(
        `Refusing to submit: ${describeAnswerMismatch(questionToSubmit.type, responseToSubmit)}`
      );
      setCurrentResponse('');
      setError(UI_TEXT.AI_SURVEY.SESSION.ERRORS.ANSWER_NOT_CAPTURED);
      submitInFlightRef.current = false;
      return;
    }

    // Preview mode: store response locally, skip all API calls and AI followup
    if (isPreviewMode) {
      const newResponse: QuestionResponse = {
        question_id: questionToSubmit.id,
        question_type: questionToSubmit.type,
        response_data: [{ type: 'original_response', value: responseToSubmit }],
        state: 'answered',
        created_at: new Date().toISOString(),
      };
      previewResponsesRef.current = [...previewResponsesRef.current.filter(r => r.question_id !== questionToSubmit.id), newResponse];
      setSession(prev => prev ? { ...prev, responses: [...prev.responses.filter(r => r.question_id !== questionToSubmit.id), newResponse] } : prev);

      const nextIndex = currentQuestionIndex + 1;
      if (nextIndex >= survey.questions.length) {
        setIsTerminated(true);
        setTerminationReason('completed');
      } else {
        setCurrentResponse('');
        setFollowupQuestion(null);
        setCurrentQuestionIndex(nextIndex);
      }
      submitInFlightRef.current = false;
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      let result;
      let shouldAdvanceQuestion = true; // Default to advancing question
      // Set by the MaxDiff / conjoint branches to move to the next set once the answer is safely
      // persisted. Deliberately not invoked on the failure path, so a failed submit leaves the
      // respondent on the set they just answered instead of silently dropping it.
      let advanceMultiStep: (() => void) | null = null;

      if (followupQuestion) {
        // This is a follow-up response. Timing is the DELTA accrued since the previous
        // submission for this question (main answer or prior follow-up), so follow-up
        // answering time counts without double-counting the already-reported buckets.
        result = await submitFollowupResponse(
          session.id,
          questionToSubmit.id,
          followupQuestion,
          responseToSubmit,
          collectActiveTimeDelta()
        );
        // A follow-up is an extra question not in the authored array — count it so the progress
        // estimate grows by one rather than overshooting.
        setFollowupsAnswered((n) => n + 1);
      } else {
        // Determine if this should advance to next question
        let actualResponseData = responseToSubmit;

        // Handle MaxDiff progression logic
        if (questionToSubmit.type === 'max_diff' && session) {
          const sets = generateAndPrepareSetsForUser(questionToSubmit.choices || [], session.id.toString(), 4, 4);

          // Evaluate and log the design quality
          const evaluation = evaluateMaxDiffDesign(sets, questionToSubmit.choices || []);
          console.log(`[MaxDiff Evaluation - Session ${session.id} - Submit Response]`, evaluation);

          const currentSetIndex = currentMaxDiffSetIndex;

          // Store the response for this set
          const newMaxDiffResponses = { ...maxDiffResponses };
          newMaxDiffResponses[currentSetIndex] = responseToSubmit;
          setMaxDiffResponses(newMaxDiffResponses);

          // Format response data for backend with set index
          actualResponseData = {
            most: responseToSubmit.most_appealing || responseToSubmit.most,
            least: responseToSubmit.least_appealing || responseToSubmit.least,
            set_index: currentSetIndex
          };

          // Check if there are more sets to show
          if (currentSetIndex < sets.length - 1) {
            shouldAdvanceQuestion = false;
            // Queued, NOT scheduled: this used to be a setTimeout fired before `await
            // submitResponse` below, so a failed submit still advanced the respondent and cleared
            // their answer — the set was silently discarded. Runs only after the submit succeeds.
            advanceMultiStep = () => {
              setCurrentMaxDiffSetIndex(currentSetIndex + 1);
              setCurrentResponse(''); // Clear response for next set
            };
          } else {
            // This was the last set, reset for next question
            setCurrentMaxDiffSetIndex(0);
            setMaxDiffResponses({});
          }
        }

        // Handle Conjoint Analysis progression logic
        if (questionToSubmit.type === 'conjoint_analysis' && questionToSubmit.conjoint_attributes) {
          const currentScenarioIndex = currentConjointScenarioIndex;

          // Store the response for this scenario
          const newConjointResponses = { ...conjointResponses };
          newConjointResponses[currentScenarioIndex] = responseToSubmit;
          setConjointResponses(newConjointResponses);

          // Find the selected profile details. Use the SAME balanced design the render path
          // shows the respondent so the stored profile_details can't drift from what was seen.
          const attributes = questionToSubmit.conjoint_attributes || [];
          const allProfiles = generateConjointProfiles(attributes);

          // Find the selected profile
          const selectedProfile = allProfiles.find(p => p.id === responseToSubmit);

          // Format response data for backend with scenario index and profile details
          actualResponseData = {
            choice: responseToSubmit,
            profile_details: selectedProfile ? selectedProfile.attributes : null,
            scenario_index: currentScenarioIndex
          };

          // Calculate optimal number of scenarios based on conjoint design principles
          // Standard practice: minimum 8-12 choice tasks for reliable utility estimation
          // Can be made configurable via question properties in the future
          const totalScenarios = questionToSubmit.conjoint_scenarios || Math.min(12, Math.max(8, attributes.length * 2));
          if (currentScenarioIndex < totalScenarios - 1) {
            shouldAdvanceQuestion = false;
            // Same deferral as MaxDiff above — never advance before the answer is persisted.
            advanceMultiStep = () => {
              setCurrentConjointScenarioIndex(currentScenarioIndex + 1);
              setCurrentResponse(''); // Clear response for next scenario
            };
          } else {
            // This was the last scenario, reset for next question
            setCurrentConjointScenarioIndex(0);
            setConjointResponses({});
          }
        }

        // Determine input mode based on current interface state
        let inputMode = null; // Default to null

        if (showTextInput) {
          inputMode = 'text';
        } else if (!showTextInput && (questionToSubmit.type === 'short_text' || questionToSubmit.type === 'long_text')) {
          // Only set to 'mic' for text input questions where voice is actually available
          inputMode = 'mic';
        }

        // This is an original response. The backend runs the synchronous redundancy look-ahead
        // ONLY after a qualitative (open-text) answer — so only then do we show the brief
        // "checking your answers" state (mirrors the follow-up analyzing screen). Structured
        // answers submit instantly with no spinner. Skip in preview (no backend call).
        const isOpenText = questionToSubmit.type === 'long_text' || questionToSubmit.type === 'short_text';
        const willCheckSkip = !isPreviewMode && shouldAdvanceQuestion
          && !!survey?.ai_redundancy_skip_enabled && isOpenText;
        if (willCheckSkip) setIsCheckingSkip(true);
        try {
          result = await submitResponse(
            session.id,
            questionToSubmit.id,
            actualResponseData,
            questionToSubmit.type,
            questionStartTime,
            shouldAdvanceQuestion,
            inputMode,
            getActiveTimings()
          );
        } finally {
          if (willCheckSkip) setIsCheckingSkip(false);
        }
      }

      setSession(result.session_data);

      // The answer is persisted — only now is it safe to move to the next set/scenario.
      advanceMultiStep?.();

      // Adaptive question scoping: the backend may return a narrowed version of the NEXT question
      // (keyed by its id). Stash it so the runner shows the adapted text when it reaches that
      // question, instead of the authored text. Absent/disabled -> unchanged behavior.
      if (result.adapted_question?.question_id && result.adapted_question.adapted_question) {
        const aq = result.adapted_question;
        setAdaptedQuestions(prev => ({ ...prev, [aq.question_id]: aq.adapted_question }));
      }

      // Check if session was terminated
      if (result.status === 'terminated') {
        setIsTerminated(true);
        setTerminationReason(result.termination_reason || null);
        setTerminationRedirectUrl(result.redirect_url || null);
        if (session) clearSavedSession(session.survey_id);
        setIsSubmitting(false);
        return; // Stop processing, show termination screen
      }

      // Check if skip logic was applied - defer until after follow-ups complete
      let skipTarget: number | null = pendingSkipTarget; // From previous follow-up cycle
      if (result.skip_applied && result.next_question_index !== undefined) {
        console.log(`Skip logic detected: will jump to question index ${result.next_question_index} after follow-ups`);
        skipTarget = result.next_question_index;
        setPendingSkipTarget(result.next_question_index);
        // AI redundancy skip: acknowledge to the respondent that we skipped questions they
        // already answered (so the progress jump doesn't read as a glitch).
        if (result.skip_reason === 'ai_skipped') {
          // next_question_index is a CANONICAL index; count the skip in DISPLAY-index space
          // so the notice is correct under question_order randomization (where canonical and
          // display order differ). Falls back to 0 if the target isn't in the order map.
          const order = session?.question_order;
          const targetDisplayPos =
            order && order.length ? order.indexOf(result.next_question_index) : result.next_question_index;
          const skippedCount =
            targetDisplayPos < 0 ? 0 : Math.max(0, targetDisplayPos - (currentQuestionIndex + 1));
          if (skippedCount > 0) {
            setAiSkipNotice({ count: skippedCount });
          }
        }
      }

      // Check if AI follow-up is enabled for this question
      // For MaxDiff questions, only trigger follow-up after the LAST set is completed
      const shouldTriggerFollowup = questionToSubmit.enable_ai_followup &&
        (followupQuestion || // Already in follow-up conversation, continue analyzing
         questionToSubmit.type !== 'max_diff' ||
         (questionToSubmit.type === 'max_diff' && currentMaxDiffSetIndex === maxDiffSets.length - 1));

      if (shouldTriggerFollowup) {
        setIsAnalyzing(true);
        setAnalysisProgress('Starting analysis...');

        try {
          // Analyze response for potential follow-up with progress tracking
          // Use responseToSubmit to ensure we get the actual user's choice/response
          let userResponseForAnalysis = typeof responseToSubmit === 'object'
            ? JSON.stringify(responseToSubmit)
            : String(responseToSubmit);

          // For MaxDiff questions, calculate top and bottom claims and format as context
          let followupContext: string | undefined = undefined;
          if (questionToSubmit.type === 'max_diff') {
            // Use the complete maxDiffResponses state which has all sets
            const completedResponses = { ...maxDiffResponses };
            completedResponses[currentMaxDiffSetIndex] = responseToSubmit;

            // Override userResponseForAnalysis to include ALL MaxDiff responses
            userResponseForAnalysis = JSON.stringify(completedResponses);

            const { topClaims, bottomClaims } = calculateMaxDiffRankedClaims(completedResponses, 3);
            console.log('MaxDiff claims calculated:', { topClaims, bottomClaims });

            // Format as context string with both top and bottom claims
            const contextParts: string[] = [];
            if (topClaims.length > 0) {
              contextParts.push(`Top ${topClaims.length} MOST appealing options:\n${topClaims.map((claim, idx) => `${idx + 1}. ${claim}`).join('\n')}`);
            }
            if (bottomClaims.length > 0) {
              contextParts.push(`Top ${bottomClaims.length} LEAST appealing options:\n${bottomClaims.map((claim, idx) => `${idx + 1}. ${claim}`).join('\n')}`);
            }
            if (contextParts.length > 0) {
              followupContext = contextParts.join('\n\n');
            }
          }

          // Extract image URLs for photo_upload questions (for vision-based followup)
          let imageUrlsForFollowup: string[] | undefined;
          if (questionToSubmit.type === 'photo_upload' && responseToSubmit?.images) {
            imageUrlsForFollowup = responseToSubmit.images
              .filter((img: { url?: string; extraction_status?: string }) => img.url)
              .map((img: { url: string }) => img.url);
            console.log('Photo upload followup with images:', imageUrlsForFollowup);
          }

          const analysis = await analyzeResponseForFollowup(
            session.id,
            questionToSubmit.id,
            questionToSubmit.text,
            userResponseForAnalysis,
            questionToSubmit.type,
            {
              timeout: ASYNC_ANALYSIS_CONFIG.POLL_TIMEOUT_MS,
              pollInterval: ASYNC_ANALYSIS_CONFIG.POLL_INTERVAL_MS,
              context: followupContext,
              imageUrls: imageUrlsForFollowup,
              onProgress: (status: string, message: string) => {
                console.log(`Analysis progress: ${status} - ${message}`);
                // Update progress message based on status
                switch (status) {
                  case ANALYSIS_TASK_STATES.PENDING:
                    setAnalysisProgress('Analysis task queued...');
                    break;
                  case ANALYSIS_TASK_STATES.PROCESSING:
                    setAnalysisProgress('AI is analyzing your response...');
                    break;
                  default:
                    setAnalysisProgress(message || 'Processing...');
                }
              }
            }
          );

          if (analysis.analysis.needs_followup && analysis.analysis.followup_question) {
            // Show follow-up question
            setFollowupQuestion(analysis.analysis.followup_question);
            // Save the original response for display during follow-up (e.g., image hotspot clicks)
            setOriginalResponse(responseToSubmit);
            // Clear current response for the follow-up text input
            setCurrentResponse('');
            setIsAnalyzing(false);
            setIsSubmitting(false);
            setAnalysisProgress('');
            return; // Don't proceed to next question yet
          }
        } catch (followupError) {
          console.error('Failed to analyze response for follow-up:', followupError);
          setAnalysisProgress('Analysis failed, continuing...');
          // Continue normally if follow-up analysis fails
        } finally {
          setIsAnalyzing(false);
          setAnalysisProgress('');
        }
      }

      // Clear follow-up state and proceed normally
      setFollowupQuestion(null);
      setOriginalResponse(null);

      // Apply pending skip logic (deferred until after follow-ups complete)
      if (skipTarget !== null) {
        console.log(`Applying deferred skip logic: jumping to question index ${skipTarget}`);
        setCurrentResponse('');
        setCurrentQuestionIndex(skipTarget);
        setPendingSkipTarget(null);
        setIsSubmitting(false);
        return;
      }

      // Only advance to next question if shouldAdvanceQuestion was true
      // For MaxDiff/Conjoint intermediate sets, we stay on the current question
      if (shouldAdvanceQuestion) {
        setCurrentResponse(''); // Clear response when advancing

        // Move to next question or complete survey
        if (currentQuestionIndex + 1 < survey.questions.length) {
          setCurrentQuestionIndex(currentQuestionIndex + 1);
        } else {
          // All questions answered, complete the survey
          await handleCompleteSession();
        }
      } else {
        // Staying on current question for intermediate sets/scenarios
        // Response is cleared by the setTimeout logic above
      }

    } catch (error) {
      console.error('Failed to submit response:', error);
      setError('Failed to submit response. Please try again.');
    } finally {
      setIsSubmitting(false);
      submitInFlightRef.current = false;
    }
  }, [session, survey, currentQuestionIndex, currentResponse, followupQuestion, originalResponse, pendingSkipTarget, getActiveTimings, collectActiveTimeDelta]);

  const handleCompleteSession = useCallback(async () => {
    if (!session) return;

    try {
      setIsSubmitting(true);
      const result = await completeSurveySession(session.id);

      // Clear saved session from localStorage on successful completion
      clearSavedSession(session.survey_id);

      // Check if panel provider redirect URL was provided
      if (result.redirect_url) {
        console.log(`Redirecting to panel provider: ${result.redirect_url}`);
        // Redirect to external panel provider URL
        window.location.href = result.redirect_url;
      } else {
        // No redirect configured, navigate to internal completion page
        navigate(`/survey-complete/${session.id}`);
      }
    } catch (error) {
      console.error('Failed to complete session:', error);
      setError('Failed to complete survey. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [session, navigate, clearSavedSession]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{UI_TEXT.AI_SURVEY.SESSION.DESCRIPTIONS.LOADING}</p>
        </div>
      </div>
    );
  }

  // Show termination screen if session was terminated. This MUST precede the conditional-skip
  // spinner guard below: a skip of the last question sets terminate while `shouldSkipCurrentQuestion`
  // can remain true for the still-current question, which would otherwise mask the termination
  // screen behind the bare spinner indefinitely. Terminated always wins.
  if (isTerminated) {
    return (
      <SurveyTermination
        reason={terminationReason || undefined}
        redirectUrl={terminationRedirectUrl || undefined}
      />
    );
  }

  // Don't render question content while skipping conditional questions (prevents flash).
  // If a skip submit failed, surface the error here so the respondent isn't stuck on a bare spinner.
  if (shouldSkipCurrentQuestion || isSkippingConditional) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          {error ? (
            <div className="max-w-md mx-auto p-6">
              <div className="bg-white rounded-xl shadow-sm p-8">
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertCircle className="w-8 h-8 text-yellow-600" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">Survey Unavailable</h2>
                <p className="text-gray-600 mb-6">{error}</p>
                <Button onClick={() => navigate('/')} variant="primary">Go Home</Button>
              </div>
            </div>
          ) : (
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          )}
        </div>
      </div>
    );
  }

  // Fraud gate: borderline respondents must pass a CAPTCHA before a session starts.
  if (showChallenge) {
    return (
      <TurnstileChallenge
        onVerified={(token) => attemptStartSession(token)}
        // If the widget can't load, surface an error rather than re-submitting
        // token-less (which the backend would just re-challenge, looping).
        onError={() => {
          setShowChallenge(false);
          setError(UI_TEXT.AI_SURVEY.FRAUD_GATE.CHALLENGE.LOAD_ERROR);
        }}
      />
    );
  }

  // Fraud gate: non-US IPs see a retryable soft gate (e.g. to disable a VPN).
  if (geoGateMessage !== null) {
    return (
      <SurveyAccessGate
        message={geoGateMessage || undefined}
        onRetry={handleAccessRetry}
        retrying={isRetryingAccess}
      />
    );
  }

  if (!session || !survey || !survey.questions) {
    // If there's an error message (e.g., quota reached), show that instead of "Survey Not Found"
    if (error) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="max-w-md mx-auto text-center p-6">
            <div className="bg-white rounded-xl shadow-sm p-8">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-yellow-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">Survey Unavailable</h2>
              <p className="text-gray-600 mb-6">{error}</p>
              <Button onClick={() => navigate('/')} variant="primary">Go Home</Button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <MessageSquare className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">{UI_TEXT.AI_SURVEY.SESSION.TITLES.SURVEY_NOT_FOUND}</h2>
          <p className="text-gray-600 mb-4">{UI_TEXT.AI_SURVEY.SESSION.DESCRIPTIONS.NOT_FOUND}</p>
          <Button onClick={() => navigate('/')}>{UI_TEXT.AI_SURVEY.SESSION.BUTTONS.GO_HOME}</Button>
        </div>
      </div>
    );
  }

  // Show welcome screen if it hasn't been dismissed yet
  if (showWelcomeScreen) {
    const welcomeData = {
      title: survey.title || survey.name || 'Survey',
      description: survey.description || 'Thank you for taking the time to participate in this survey. Our AI will ask follow-up questions based on your answers to gather deeper insights. There are no right or wrong answers - we just want to hear your honest thoughts and opinions.',
      instructions: survey.instructions || 'You can answer using either voice or text input and switch between them at any time. For the best voice experience, please find a quiet space where you can speak clearly.'
    };

    return (
      <SurveyWelcomeScreen
        welcomeData={welcomeData}
        onStart={handleStartSurvey}
      />
    );
  }

  if (showConsentScreen) {
    return <SurveyConsentScreen onAccept={handleAcceptConsent} />;
  }

  // Experimental: once past the welcome screen AND the consent screen, a conversational /
  // discussion-guide survey hands the entire respondent experience to the voice moderator flow.
  // This MUST come after the consent check — the session is created on load (id > 0 before
  // consent), so checking conversational first would skip consent entirely. Traditional surveys
  // (the default, survey_style absent or 'traditional') never reach this branch and are completely
  // unaffected. Requires a real persisted session (the moderator endpoints need it), so preview
  // mode (id < 0) falls through.
  if (survey?.survey_style === 'conversational' && session && session.id > 0) {
    return <ConversationalSurveyFlow survey={survey} sessionId={session.id} />;
  }

  // currentQuestion is computed via useMemo with question_order support
  // If currentQuestion is null, show loading or error state
  if (!currentQuestion) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Loading question...</p>
        </div>
      </div>
    );
  }

  const isLastQuestion = currentQuestionIndex === survey.questions.length - 1;

  // Enhanced validation logic for all question types
  const canProceed = (() => {
    if (currentResponse === '' || currentResponse === null || currentResponse === undefined) {
      return false;
    }

    // For follow-up questions, only check if there's a non-empty text response
    if (followupQuestion) {
      return typeof currentResponse === 'string' && currentResponse.trim().length > 0;
    }

    // Shape gate before the per-type completeness checks below. Catches an answer belonging to a
    // DIFFERENT question type sitting in the shared state — previously nothing did, because
    // long_text/short_text have no case in this switch and fell through to `default: return true`.
    if (!isValidAnswerFor(currentQuestion.type, currentResponse)) {
      return false;
    }

    switch (currentQuestion.type) {
      case 'matrix_rating':
        // Matrix questions require all items to have ratings
        if (typeof currentResponse !== 'object' || currentResponse === null) return false;
        const matrixItems = currentQuestion.matrix_rows || currentQuestion.choices || [];
        return matrixItems.every((item: string) =>
          currentResponse[item] !== undefined && currentResponse[item] !== null
        );

      case 'max_diff':
        // MaxDiff requires both most and least appealing selections
        if (typeof currentResponse !== 'object' || currentResponse === null) return false;
        return currentResponse.most_appealing && currentResponse.least_appealing;
      
      case 'image_hotspot':
        // Image hotspot requires clicks array with required number of selections
        if (typeof currentResponse !== 'object' || currentResponse === null) return false;
        const clicks = currentResponse.clicks;
        if (!Array.isArray(clicks)) return false;
        return clicks.length >= (currentQuestion.ranking_count || 3);

      case 'info_screen':
        // Info screens always have a valid response (no input required)
        return true;

      case 'conjoint_analysis':
        // Conjoint analysis requires a profile selection
        return typeof currentResponse === 'string' && currentResponse.startsWith('profile_');

      case 'multiple_choice':
        // If has media URLs in EXPLICIT choice-level mode (legacy), it's an image/video choice question
        if (currentQuestion.media_urls && currentQuestion.media_urls.length > 0 && currentQuestion.media_display_mode === 'choices') {
          return currentResponse?.selected_indices && currentResponse.selected_indices.length > 0;
        }
        // Multiple choice requires at least one selection (handles both question-level media and no media)
        return Array.isArray(currentResponse) && currentResponse.length > 0;

      case 'single_choice':
        // If has media URLs in EXPLICIT choice-level mode (legacy), it's an image/video choice question
        if (currentQuestion.media_urls && currentQuestion.media_urls.length > 0 && currentQuestion.media_display_mode === 'choices') {
          return currentResponse?.selected_indices && currentResponse.selected_indices.length > 0;
        }
        // Single choice requires a selection (handles both question-level media and no media)
        return currentResponse != null;

      case 'ranking':
        // Ranking requires the minimum number of selections
        const rankingCount = currentQuestion.ranking_count || 3;
        return Array.isArray(currentResponse) && currentResponse.length >= rankingCount;

      case 'percentage_allocation':
        // Percentage allocation requires total to equal target
        if (typeof currentResponse !== 'object' || currentResponse === null) return false;
        const target = currentQuestion.allocation_target || 100;
        const allocOpts = currentQuestion.allocation_options || currentQuestion.choices || [];
        const total = Object.values(currentResponse as Record<string, number>).reduce((sum: number, val) => sum + (val || 0), 0);
        // Check allow_zero constraint - if allow_zero is false, all options must have value > 0
        const allowZeroCheck = currentQuestion.allow_zero !== false; // Default true
        if (!allowZeroCheck && allocOpts.length > 0) {
          const hasZeroValue = allocOpts.some((opt: string) => {
            const val = (currentResponse as Record<string, number>)[opt];
            return val === 0 || val === undefined || val === null;
          });
          if (hasZeroValue) return false;
        }
        return total === target;

      case 'photo_upload':
        return isPhotoUploadResponseValid(currentResponse);

      default:
        // For all other question types, basic non-empty validation
        return true;
    }
  })();

  const renderQuestionInput = () => {
    // Also true once transcription has failed — the mic is pointless if nothing can transcribe it.
    const voiceDisabled = !!currentQuestion?.disable_voice_input || transcriptionUnavailable;
    const textDisabled = !!currentQuestion?.disable_text_input;

    // Helper to render question-level media for question types that don't have special media handling
    const renderSimpleQuestionMedia = () => {
      if (!orderedQuestionMediaUrls || orderedQuestionMediaUrls.length === 0) return null;

      const mediaCount = orderedQuestionMediaUrls.length;

      return (
        <div className="mb-6">
          <div
            className={`grid gap-4 ${
              mediaCount === 1 ? 'grid-cols-1 max-w-2xl mx-auto' : 'grid-cols-1 md:grid-cols-2'
            }`}
          >
            {orderedQuestionMediaUrls.map((url: string, index: number) => {
              const isVideo = isVideoUrl(url);
              const fullUrl = url.startsWith('/api/') ? `${import.meta.env.VITE_API_URL || ''}${url}` : url;
              // Scope the key/state to the question id: on navigation React remounts the node
              // (no stale decoded frame) and the image starts unloaded (skeleton, not the
              // previous question's image showing through).
              const mediaKey = `${currentQuestion?.id ?? 'q'}-media-${index}`;
              const isLoaded = questionMediaLoaded[mediaKey] === true;
              const hasErrored = questionMediaErrored[mediaKey] === true;

              return (
                <div
                  key={mediaKey}
                  className="relative border border-gray-200 rounded-lg overflow-hidden bg-gray-50 min-h-[200px] flex items-center justify-center"
                >
                  {/* Loading skeleton — covers the frame until this question's media paints,
                      so a question switch never flashes the previous question's images.
                      Hidden once errored too: the skeleton is `absolute inset-0 z-10`, so
                      leaving it up would cover the failure message (and, on a <video>, would
                      swallow clicks on the native controls). */}
                  {!isLoaded && !hasErrored && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-gray-100 animate-pulse">
                      <div className="w-6 h-6 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}
                  {hasErrored && (
                    <div className="absolute inset-0 z-20 flex items-center justify-center bg-gray-100 p-4">
                      <span className="text-sm text-gray-500 text-center">
                        {isVideo ? 'Failed to load video' : 'Failed to load image'}
                      </span>
                    </div>
                  )}
                  {isVideo ? (
                    url.includes('youtube.com') || url.includes('youtu.be') ? (
                      <div className="aspect-video w-full">
                        <iframe
                          src={url.includes('youtube.com') ? url.replace('watch?v=', 'embed/') : url.replace('youtu.be/', 'youtube.com/embed/')}
                          className={`w-full h-full transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
                          allowFullScreen
                          title={`Video ${index + 1}`}
                          onLoad={() => markQuestionMediaLoaded(mediaKey)}
                        />
                      </div>
                    ) : (
                      <video
                        src={fullUrl}
                        className={`w-full h-auto max-h-[400px] transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
                        controls
                        // `loadeddata` only fires once a frame has actually decoded, which
                        // never happens under preload="none" (iOS Safari / data-saver) until
                        // the user hits play — but the skeleton overlay sits above the video
                        // and swallows that click, so the respondent gets permanently stuck.
                        // Clear on metadata/canplay too, which fire without decoding a frame.
                        preload="metadata"
                        onLoadedMetadata={() => markQuestionMediaLoaded(mediaKey)}
                        onCanPlay={() => markQuestionMediaLoaded(mediaKey)}
                        onLoadedData={() => markQuestionMediaLoaded(mediaKey)}
                        onError={() => { markQuestionMediaLoaded(mediaKey); markQuestionMediaErrored(mediaKey); }}
                      />
                    )
                  ) : (
                    <img
                      src={fullUrl}
                      alt={`Question media ${index + 1}`}
                      className={`w-full h-auto max-h-[400px] object-contain transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
                      // Mark paint (hides skeleton, reveals image) AND report to the stimulus gate
                      // so the answer UI unlocks on paint, not just the 8s fail-open timeout.
                      // A cached image can complete before onLoad attaches, hence the ref check.
                      ref={(el) => { if (el?.complete) { markQuestionMediaLoaded(mediaKey); markStimulusResolved(url); } }}
                      onLoad={() => { markQuestionMediaLoaded(mediaKey); markStimulusResolved(url); }}
                      // Still resolve the stimulus gate on error — failing open keeps the
                      // answer UI usable rather than stranding the respondent on a dead image.
                      onError={() => { markQuestionMediaLoaded(mediaKey); markQuestionMediaErrored(mediaKey); markStimulusResolved(url); }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      );
    };

    // If there's a follow-up question, always show text input interface
    if (followupQuestion) {
      // Check if the original question has media to display
      // For text questions: always show media if available
      // For single/multiple choice: only show media if NOT in explicit 'choices' mode (legacy)
      // For image_hotspot: always show the reference image
      const isTextQuestion = currentQuestion.type === 'short_text' || currentQuestion.type === 'long_text';
      const isChoiceQuestion = currentQuestion.type === 'single_choice' || currentQuestion.type === 'multiple_choice';
      const isImageHotspot = currentQuestion.type === 'image_hotspot';
      const displayMode = currentQuestion.media_display_mode || 'question'; // Default to question-level for new system
      const isQuestionLevelMedia = displayMode !== 'choices'; // Question-level unless explicitly 'choices'

      // Build image hotspot URL if applicable
      const hotspotImageUrl = isImageHotspot && currentQuestion.image_url
        ? (currentQuestion.image_url.startsWith('http') 
            ? currentQuestion.image_url 
            : `${import.meta.env.VITE_API_URL || ''}/api/question-images/public/${survey.id}/${currentQuestion.image_url.split('/').pop()}`)
        : null;

      const hasMedia = currentQuestion.media_urls && currentQuestion.media_urls.length > 0 &&
        (isTextQuestion || (isChoiceQuestion && isQuestionLevelMedia));
      
      // Get user's selections from originalResponse for image hotspot
      // originalResponse stores the user's response data (e.g., { clicks: [...] } for image_hotspot)
      const hotspotClicks = isImageHotspot && originalResponse && typeof originalResponse === 'object' && originalResponse.clicks
        ? (originalResponse.clicks as Array<{ rank: number; x: number; y: number; product_id: string; product_label: string; timestamp_ms: number }>)
        : [];
      const boundingBoxes = isImageHotspot ? (currentQuestion.bounding_boxes || []) : [];
      
      // Get rank color for visual feedback
      const getRankColor = (rank: number): string => {
        const colors = [
          'bg-teal-500',    // Rank 1
          'bg-blue-500',    // Rank 2
          'bg-purple-500',  // Rank 3
          'bg-orange-500',  // Rank 4
          'bg-pink-500',    // Rank 5
        ];
        return colors[(rank - 1) % colors.length];
      };

      // Image hotspot reference image content with selections overlay
      const hotspotContent = hotspotImageUrl ? (
        <div className="space-y-4 mb-6">
          {/* Reference Image Badge */}
          <div className="flex items-center justify-between">
            <span className="inline-flex items-center rounded-md bg-teal-100 px-2 py-1 text-xs font-medium text-teal-700">
              Reference Image
            </span>
            <span className="text-xs text-gray-500">
              Your selections are highlighted
            </span>
          </div>

          {/* Reference Image with Selections Overlay */}
          <div className="border border-gray-200 rounded-lg p-4 shadow-sm flex flex-col items-center">
            <div className="relative inline-block rounded-md overflow-hidden">
              <img
                src={hotspotImageUrl}
                alt="Reference image for follow-up question"
                className="max-w-full h-auto max-h-[400px] object-contain rounded-md"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const parent = target.parentElement;
                  if (parent) {
                    parent.innerHTML = '<div class="w-full h-48 flex items-center justify-center text-sm text-gray-500">Failed to load image</div>';
                  }
                }}
              />
              
              {/* Bounding boxes overlay - show selected products */}
              {boundingBoxes.map((box: { id: string; label: string; x: number; y: number; width: number; height: number }) => {
                const isSelected = hotspotClicks.some(click => click.product_id === box.id);
                const clickForBox = hotspotClicks.find(click => click.product_id === box.id);

                if (!isSelected) return null; // Only show selected boxes

                return (
                  <div
                    key={box.id}
                    className="absolute border-2 border-teal-500 bg-teal-500/20 pointer-events-none"
                    style={{
                      left: `${box.x * 100}%`,
                      top: `${box.y * 100}%`,
                      width: `${box.width * 100}%`,
                      height: `${box.height * 100}%`
                    }}
                  >
                    {/* Rank indicator */}
                    {clickForBox && (
                      <div
                        className={`absolute -top-3 -left-3 w-6 h-6 ${getRankColor(clickForBox.rank)} text-white text-xs font-bold rounded-full flex items-center justify-center shadow-md border-2 border-white`}
                      >
                        {clickForBox.rank}
                      </div>
                    )}
                    {/* Product label */}
                    <div className="absolute -bottom-6 left-0 bg-gray-800 text-white text-xs px-2 py-0.5 rounded whitespace-nowrap">
                      {box.label}
                    </div>
                  </div>
                );
              })}

              {/* Free click mode markers - show click positions */}
              {currentQuestion.free_click_mode && hotspotClicks.map((click) => (
                <div
                  key={click.rank}
                  className="absolute pointer-events-none"
                  style={{
                    left: `${click.x * 100}%`,
                    top: `${click.y * 100}%`,
                    transform: 'translate(-50%, -50%)'
                  }}
                >
                  <div
                    className={`w-7 h-7 ${getRankColor(click.rank)} text-white text-xs font-bold rounded-full flex items-center justify-center shadow-lg border-2 border-white`}
                  >
                    {click.rank}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Selection Summary */}
          {hotspotClicks.length > 0 && (
            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 mt-4 w-full max-w-md">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Your Selections:</h4>
              <div className="space-y-1">
                {hotspotClicks.map((click) => (
                  <div key={click.rank} className="flex items-center gap-2 text-sm">
                    <span className={`w-5 h-5 ${getRankColor(click.rank)} text-white text-xs font-bold rounded-full flex items-center justify-center`}>
                      {click.rank}
                    </span>
                    <span className="text-gray-700">
                      {currentQuestion.free_click_mode || !click.product_label
                        ? `Location (${(click.x * 100).toFixed(0)}%, ${(click.y * 100).toFixed(0)}%)`
                        : click.product_label}
                    </span>
                    <span className="text-gray-400 text-xs">
                      ({(click.timestamp_ms / 1000).toFixed(1)}s)
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : null;
      
      const mediaContent = hasMedia ? (
        <div className="space-y-4 mb-6">
          {/* Media Type Badge */}
          <div className="flex items-center justify-between">
            <span className="inline-flex items-center rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600">
              Follow-up question
            </span>
          </div>

          {/* Media Grid */}
          <div className="grid gap-6 grid-cols-1 max-w-4xl mx-auto">
            {orderedQuestionMediaUrls.map((url: string, index: number) => {
              // Determine if it's image or video
              const isVideo = isVideoUrl(url);

              return (
                <div key={`${currentQuestion.id}-media-${index}`} className="border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
                  <div className="relative rounded-md overflow-hidden">
                    {isVideo ? (
                      url.includes('youtube.com') || url.includes('youtu.be') ? (
                        // YouTube Embed
                        <div className="aspect-video">
                          <iframe
                            src={url.includes('youtube.com')
                              ? url.replace('watch?v=', 'embed/')
                              : url.replace('youtu.be/', 'youtube.com/embed/')
                            }
                            className="w-full h-full rounded-md"
                            allowFullScreen
                            title={`Video ${index + 1}`}
                          />
                        </div>
                      ) : (
                        // Regular Video
                        <video
                          src={getFullMediaUrl(url)}
                          className="w-full h-auto max-h-[600px] rounded-md"
                          controls
                          onError={(e) => {
                            const target = e.target as HTMLVideoElement;
                            target.style.display = 'none';
                            const parent = target.parentElement;
                            if (parent) {
                              parent.innerHTML = '<div class="w-full h-48 flex items-center justify-center text-sm text-gray-500">Failed to load video</div>';
                            }
                          }}
                        />
                      )
                    ) : (
                      // Image - show full image without cropping
                      <img
                        src={getFullMediaUrl(url)}
                        alt={`Image ${index + 1} for survey question`}
                        className="w-full h-auto max-h-[500px] object-contain rounded-md"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          const parent = target.parentElement;
                          if (parent) {
                            parent.innerHTML = '<div class="w-full h-48 flex items-center justify-center text-sm text-gray-500">Failed to load image</div>';
                          }
                        }}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : null;

      return (
        <div className="space-y-6">
          {/* Image Hotspot Reference - shown above text input for follow-up questions */}
          {hotspotContent}
          
          {/* Media Content - shown above text input for follow-up questions */}
          {mediaContent}

          {!showTextInput ? (
            // Voice Input Mode (Default)
            <div className="text-center space-y-6">
              <div className="flex flex-col items-center space-y-4">
                <div className="relative">
                  {/* Pulsing ring animation for inactive state */}
                  {!isRecording && !isTranscribing && (
                    <div className="absolute inset-0 w-24 h-24 rounded-full bg-blue-400 animate-ping opacity-20"></div>
                  )}

                  {/* Recording pulse ring */}
                  {isRecording && (
                    <div className="absolute inset-0 w-24 h-24 rounded-full bg-red-400 animate-pulse opacity-30"></div>
                  )}

                  <button
                    type="button"
                    onClick={micStatus !== 'idle' ? stopRecording : startRecording}
                    disabled={isTranscribing}
                    className={`relative w-24 h-24 rounded-full transition-all duration-300 flex items-center justify-center shadow-lg ${
                      isRecording
                        ? 'bg-red-500 text-white scale-110 shadow-red-200'
                        : isTranscribing
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-blue-500 text-white hover:bg-blue-600 hover:scale-105 active:scale-95 shadow-blue-200'
                    }`}
                    title={isRecording ? 'Click to stop recording' : isTranscribing ? 'Transcribing...' : 'Click to start recording'}
                  >
                  {isTranscribing ? (
                    <div className="w-8 h-8 border-4 border-gray-500 border-t-transparent rounded-full animate-spin" />
                  ) : isRecording ? (
                    /* Real-time audio waveform with flowing wave motion */
                    <div className="flex items-center justify-center space-x-0.5 h-12 w-12 relative">
                      {audioLevels.map((level, i) => {
                        // Start at 1px and scale up with audio
                        const baseHeight = 1 + (level * 17); // More visible variation

                        // Different position variation patterns based on volume level
                        const quietVariation = [0, 3, 6, 9, 6, 3, 0][i]; // Small variations when quiet
                        const loudVariation = [0, 6, 12, 18, 12, 6, 0][i]; // Dramatic variations when loud
                        const positionVariation = level < 1 ? quietVariation : loudVariation;
                        const positionOffset = positionVariation * level;

                        const dynamicHeight = baseHeight + positionOffset;

                        return (
                          <div
                            key={i}
                            className="w-1.5 bg-white rounded-full opacity-95 transition-all duration-75 ease-out"
                            style={{
                              height: `${dynamicHeight}px`,
                            }}
                          />
                        );
                      })}
                    </div>
                  ) : (
                    <Mic className="w-8 h-8" />
                  )}
                </button>
                </div>

                <div className="text-center">
                  <p className="text-lg font-medium text-gray-900 mb-2">
                    {micStatus === 'warming'
                      ? UI_TEXT.AI_SURVEY.SESSION.MESSAGES.MIC_WARMING
                      : micStatus === 'listening'
                      ? UI_TEXT.AI_SURVEY.SESSION.MESSAGES.LISTENING
                      : isTranscribing
                      ? UI_TEXT.AI_SURVEY.SESSION.MESSAGES.CONVERTING_SPEECH
                      : UI_TEXT.AI_SURVEY.SESSION.MESSAGES.TAP_TO_SPEAK
                    }
                  </p>
                  {micStatus === 'idle' && !isTranscribing && (
                    <p className="text-sm text-gray-500">
                      {UI_TEXT.AI_SURVEY.SESSION.MESSAGES.VOICE_CONVERTED}
                    </p>
                  )}
                  {(isRecording || isTranscribing) && (
                    <div className="flex items-center justify-center space-x-2 text-sm text-gray-600">
                      {isRecording && (
                        <>
                          <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                          <span>{UI_TEXT.AI_SURVEY.SESSION.MESSAGES.RECORDING_TAP_AGAIN}</span>
                        </>
                      )}
                      {isTranscribing && (
                        <>
                          <div className="w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                          <span>{UI_TEXT.AI_SURVEY.SESSION.MESSAGES.PROCESSING_SPEECH}</span>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Show transcribed text if available. Guarded to a STRING: a non-text
                  answer (e.g. a photo_upload {images,...} object briefly re-injected by
                  the upload poll) must never render as a React child (React error #31). */}
              {typeof currentResponse === 'string' && currentResponse && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-2">{UI_TEXT.AI_SURVEY.SESSION.MESSAGES.YOUR_RESPONSE}</p>
                  <p className="text-gray-900">{typeof currentResponse === 'string' ? currentResponse : ''}</p>
                </div>
              )}

              {/* Alternative text input button */}
              {!textDisabled && (
                <button
                  type="button"
                  onClick={() => setShowTextInput(true)}
                  className="inline-flex items-center space-x-1 text-xs text-gray-400 hover:text-gray-600 transition-colors mt-4"
                >
                  <Keyboard className="w-3 h-3" />
                  <span>{UI_TEXT.AI_SURVEY.SESSION.MESSAGES.SWITCH_TO_TEXT}</span>
                </button>
              )}
            </div>
          ) : (
            // Text Input Mode (Alternative)
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">{UI_TEXT.AI_SURVEY.SESSION.MESSAGES.TYPE_YOUR_ANSWER}</h3>
                {!voiceDisabled && (
                  <button
                    type="button"
                    onClick={() => setShowTextInput(false)}
                    className="inline-flex items-center space-x-2 text-sm text-blue-600 hover:text-blue-700 transition-colors"
                  >
                    <Mic className="w-4 h-4" />
                    <span>{UI_TEXT.AI_SURVEY.SESSION.MESSAGES.SWITCH_TO_VOICE}</span>
                  </button>
                )}
              </div>
              <textarea
                value={typeof currentResponse === 'string' ? currentResponse : ''}
                onChange={(e) => handleResponseChange(e.target.value)}
                placeholder={UI_TEXT.AI_SURVEY.SESSION.PLACEHOLDERS.OPEN_ENDED}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows={4}
                autoFocus
              />
            </div>
          )}
        </div>
      );
    }

    switch (currentQuestion.type) {
      case 'long_text':
      case 'short_text':
        return (
          <div className="space-y-6">
            {/* Question-level media — info-screen-style responsive grid, shown above text input */}
            {renderSimpleQuestionMedia()}

            {!showTextInput ? (
              // Voice Input Mode (Default)
              <div className="text-center space-y-6">
                <div className="flex flex-col items-center space-y-4">
                  <div className="relative">
                    {/* Pulsing ring animation for inactive state */}
                    {!isRecording && !isTranscribing && (
                      <div className="absolute inset-0 w-24 h-24 rounded-full bg-blue-400 animate-ping opacity-20"></div>
                    )}
                    
                    {/* Recording pulse ring */}
                    {isRecording && (
                      <div className="absolute inset-0 w-24 h-24 rounded-full bg-red-400 animate-pulse opacity-30"></div>
                    )}

                    <button
                      type="button"
                      onClick={micStatus !== 'idle' ? stopRecording : startRecording}
                      disabled={isTranscribing}
                      className={`relative w-24 h-24 rounded-full transition-all duration-300 flex items-center justify-center shadow-lg ${
                        isRecording
                          ? 'bg-red-500 text-white scale-110 shadow-red-200'
                          : isTranscribing
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-blue-500 text-white hover:bg-blue-600 hover:scale-105 active:scale-95 shadow-blue-200'
                      }`}
                      title={isRecording ? 'Click to stop recording' : isTranscribing ? 'Transcribing...' : 'Click to start recording'}
                    >
                    {isTranscribing ? (
                      <div className="w-8 h-8 border-4 border-gray-500 border-t-transparent rounded-full animate-spin" />
                    ) : isRecording ? (
                      /* Real-time audio waveform with flowing wave motion */
                      <div className="flex items-center justify-center space-x-0.5 h-12 w-12 relative">
                        {audioLevels.map((level, i) => {
                          // Start at 1px and scale up with audio
                          const baseHeight = 1 + (level * 17); // More visible variation

                          // Different position variation patterns based on volume level
                          const quietVariation = [0, 3, 6, 9, 6, 3, 0][i]; // Small variations when quiet
                          const loudVariation = [0, 6, 12, 18, 12, 6, 0][i]; // Dramatic variations when loud
                          const positionVariation = level < 1 ? quietVariation : loudVariation;
                          const positionOffset = positionVariation * level;

                          const dynamicHeight = baseHeight + positionOffset;

                          return (
                            <div
                              key={i}
                              className="w-1.5 bg-white rounded-full opacity-95 transition-all duration-75 ease-out"
                              style={{
                                height: `${dynamicHeight}px`,
                              }}
                            />
                          );
                        })}
                      </div>
                    ) : (
                      <Mic className="w-8 h-8" />
                    )}
                  </button>
                  </div>
                  
                  <div className="text-center">
                    <p className="text-lg font-medium text-gray-900 mb-2">
                      {micStatus === 'warming'
                        ? UI_TEXT.AI_SURVEY.SESSION.MESSAGES.MIC_WARMING
                        : micStatus === 'listening'
                        ? UI_TEXT.AI_SURVEY.SESSION.MESSAGES.LISTENING
                        : isTranscribing
                        ? UI_TEXT.AI_SURVEY.SESSION.MESSAGES.CONVERTING_SPEECH
                        : UI_TEXT.AI_SURVEY.SESSION.MESSAGES.TAP_TO_SPEAK
                      }
                    </p>
                    {micStatus === 'idle' && !isTranscribing && (
                      <p className="text-sm text-gray-500">
                        {UI_TEXT.AI_SURVEY.SESSION.MESSAGES.VOICE_CONVERTED}
                      </p>
                    )}
                    {(isRecording || isTranscribing) && (
                      <div className="flex items-center justify-center space-x-2 text-sm text-gray-600">
                        {isRecording && (
                          <>
                            <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                            <span>{UI_TEXT.AI_SURVEY.SESSION.MESSAGES.RECORDING_TAP_AGAIN}</span>
                          </>
                        )}
                        {isTranscribing && (
                          <>
                            <div className="w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                            <span>{UI_TEXT.AI_SURVEY.SESSION.MESSAGES.PROCESSING_SPEECH}</span>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Show transcribed text if available. Guarded to a STRING (see the
                    follow-up block above) so a non-text answer never renders as a child. */}
                {typeof currentResponse === 'string' && currentResponse && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <p className="text-sm text-gray-600 mb-2">{UI_TEXT.AI_SURVEY.SESSION.MESSAGES.YOUR_RESPONSE}</p>
                    <p className="text-gray-900">{typeof currentResponse === 'string' ? currentResponse : ''}</p>
                  </div>
                )}

                {/* Alternative text input button */}
                {!textDisabled && (
                  <button
                    type="button"
                    onClick={() => setShowTextInput(true)}
                    className="inline-flex items-center space-x-1 text-xs text-gray-400 hover:text-gray-600 transition-colors mt-4"
                  >
                    <Keyboard className="w-3 h-3" />
                    <span>{UI_TEXT.AI_SURVEY.SESSION.MESSAGES.SWITCH_TO_TEXT}</span>
                  </button>
                )}
              </div>
            ) : (
              // Text Input Mode (Alternative)
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900">{UI_TEXT.AI_SURVEY.SESSION.MESSAGES.TYPE_YOUR_ANSWER}</h3>
                  {!voiceDisabled && (
                    <button
                      type="button"
                      onClick={() => setShowTextInput(false)}
                      className="inline-flex items-center space-x-2 text-sm text-blue-600 hover:text-blue-700 transition-colors"
                    >
                      <Mic className="w-4 h-4" />
                      <span>{UI_TEXT.AI_SURVEY.SESSION.MESSAGES.SWITCH_TO_VOICE}</span>
                    </button>
                  )}
                </div>
                <textarea
                  value={typeof currentResponse === 'string' ? currentResponse : ''}
                  onChange={(e) => handleResponseChange(e.target.value)}
                  placeholder={currentQuestion.type === 'short_text' ? "Enter your answer..." : UI_TEXT.AI_SURVEY.SESSION.PLACEHOLDERS.OPEN_ENDED}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={4}
                  autoFocus
                />
              </div>
            )}
          </div>
        );
      
      case 'single_choice':
        // Check if this is a media question and determine display mode
        const hasMediaChoice = currentQuestion.media_urls && currentQuestion.media_urls.length > 0;
        // Only use choice-level mode if EXPLICITLY set to 'choices' (legacy surveys)
        // New system: media_urls = question-level, option_media = per-option
        const displayMode = currentQuestion.media_display_mode || 'question'; // Default to question-level for new system
        const isQuestionMode = displayMode === 'question';

        // Question-level media mode (media displayed below question text)
        if (hasMediaChoice && isQuestionMode) {
          return (
            <div className="space-y-4">
              {/* Question-level media — info-screen-style responsive grid. Wires the stimulus
                  gate so choices stay locked until the images paint. */}
              {renderSimpleQuestionMedia()}

              {/* Choices — with option_media if present */}
              {/* Answering is locked until the stimulus above has actually painted: these
                  buttons submit on click, so an early tap would rate an image the respondent
                  never saw. <fieldset disabled> also blocks keyboard activation, which a
                  pointer-events rule would not. Fails open via useStimulusReady. */}
              <fieldset
                disabled={!stimulusReady}
                aria-busy={!stimulusReady}
                className={`space-y-3 border-0 p-0 m-0 transition-opacity duration-200 ${
                  stimulusReady ? 'opacity-100' : 'opacity-40 cursor-progress'
                }`}
              >
                {!stimulusReady && (
                  <p className="text-sm text-gray-500 italic" role="status">
                    {UI_TEXT.AI_SURVEY.SESSION.LOADING_STIMULUS}
                  </p>
                )}
                {randomizedChoices.map((choice: string, index: number) => {
                  const isSelected = currentResponse === choice;
                  const origIdx = currentQuestion.choices?.indexOf(choice) ?? index;
                  const choiceMedia = currentQuestion.option_media?.[String(origIdx)] || currentQuestion.option_media?.[origIdx] || [];

                  if (choiceMedia.length > 0) {
                    return (
                      <button
                        key={choice}
                        type="button"
                        onClick={async () => {
                          handleResponseChange(choice);
                          handleSubmitResponse(choice);
                        }}
                        className={`w-full text-left rounded-xl border-2 transition-all duration-200 overflow-hidden ${
                          isSelected
                            ? 'border-blue-500 bg-blue-50 shadow-md ring-2 ring-blue-200'
                            : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'
                        }`}
                      >
                        <div className={`px-4 py-3 flex items-center justify-between ${isSelected ? 'bg-blue-100' : 'bg-gray-50'}`}>
                          <span className={`font-medium ${isSelected ? 'text-blue-900' : 'text-gray-900'}`}>{choice}</span>
                          {isSelected && (
                            <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                              <Check className="w-4 h-4 text-white" />
                            </div>
                          )}
                        </div>
                        <div className="p-3">
                          <div className={`grid gap-2 ${choiceMedia.length === 1 ? 'grid-cols-1' : choiceMedia.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
                            {choiceMedia.map((url: string, mediaIdx: number) => (
                              <div key={mediaIdx} className={OPTION_MEDIA_CONTAINER_CLASS}>
                                <img
                                  src={getFullMediaUrl(url)}
                                  alt={`${choice} media ${mediaIdx + 1}`}
                                  className={OPTION_MEDIA_IMG_CLASS}
                                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      </button>
                    );
                  }

                  return (
                    <button
                      key={choice}
                      type="button"
                      onClick={async () => {
                        handleResponseChange(choice);
                        handleSubmitResponse(choice);
                      }}
                      className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all duration-200 ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50 text-blue-900'
                          : 'border-gray-300 bg-white text-gray-900 hover:border-gray-400 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span>{choice}</span>
                        {isSelected && (
                          <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                            <Check className="w-3 h-3 text-white" />
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}

                {/* Other option if enabled */}
                {currentQuestion.allow_other && (
                  <div className="space-y-2">
                    <button
                      type="button"
                      onClick={() => {
                        handleResponseChange({ choice: 'Other', other_text: '' });
                      }}
                      className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all duration-200 ${
                        typeof currentResponse === 'object' && currentResponse?.choice === 'Other'
                          ? 'border-blue-500 bg-blue-50 text-blue-900'
                          : 'border-gray-300 bg-white text-gray-900 hover:border-gray-400 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span>{UI_TEXT.AI_SURVEY.SESSION.CHOICES.OTHER}</span>
                        {typeof currentResponse === 'object' && currentResponse?.choice === 'Other' && (
                          <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                            <Check className="w-3 h-3 text-white" />
                          </div>
                        )}
                      </div>
                    </button>

                    {/* Text input for Other option */}
                    {typeof currentResponse === 'object' && currentResponse?.choice === 'Other' && (
                      <input
                        type="text"
                        placeholder={UI_TEXT.AI_SURVEY.SESSION.PLACEHOLDERS.OTHER_SPECIFY}
                        value={currentResponse?.other_text || ''}
                        onChange={(e) => {
                          handleResponseChange({
                            choice: 'Other',
                            other_text: e.target.value
                          });
                        }}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        autoFocus
                      />
                    )}
                  </div>
                )}
              </fieldset>
            </div>
          );
        }

        // Choice-level media mode (media displayed with each choice) - existing/legacy behavior
        // Only use this if option_media is NOT present (option_media is the new preferred approach)
        const hasNewOptionMedia = currentQuestion.option_media && Object.keys(currentQuestion.option_media).length > 0;
        if (hasMediaChoice && !isQuestionMode && !hasNewOptionMedia) {
          const displayUrls = orderedQuestionMediaUrls;
          const storageUrls = currentQuestion.media_urls || [];

          return (
            <div className="space-y-4">
              {/* Instructions */}
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  Click on an image to select it.
                </p>
              </div>

              {/* Image Grid */}
              <div className={`grid gap-6 ${
                displayUrls.length === 1
                  ? 'grid-cols-1 max-w-2xl mx-auto'
                  : displayUrls.length === 2
                  ? 'grid-cols-1 lg:grid-cols-2 max-w-4xl mx-auto'
                  : 'grid-cols-1 lg:grid-cols-2 max-w-5xl mx-auto'
              }`}>
                {displayUrls.map((url: string, displayIdx: number) => {
                  const originalIdx = questionMediaOriginalIndices[displayIdx];
                  const isSelected =
                    currentResponse?.selected_indices?.includes(originalIdx) ||
                    currentResponse?.selected_index === originalIdx;

                  return (
                    <div
                      key={`${currentQuestion.id}-image-${originalIdx}`}
                      className={`cursor-pointer transition-all duration-200 hover:shadow-lg border-2 rounded-lg p-4 ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50 shadow-md'
                          : 'border-gray-300 hover:border-gray-400 hover:shadow-md'
                      }`}
                      onClick={() => {
                        const response = {
                          selected_indices: [originalIdx],
                          selected_urls: [storageUrls[originalIdx]],
                          selected_index: originalIdx
                        };
                        handleResponseChange(response);
                        handleSubmitResponse(response);
                      }}
                    >
                      {/* Selection Indicator */}
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-gray-700">
                          {currentQuestion.choices?.[originalIdx] || `Image ${originalIdx + 1}`}
                        </span>
                        {isSelected && (
                          <Check className="h-5 w-5 text-blue-500" />
                        )}
                      </div>

                      {/* Image Container */}
                      <div className="relative rounded-md overflow-hidden">
                        <img
                          src={getFullMediaUrl(url)}
                          alt={`Image ${originalIdx + 1} - Choice option`}
                          className="w-full h-auto max-h-80 object-contain rounded-md"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            const parent = target.parentElement;
                            if (parent) {
                              parent.innerHTML = '<div class="w-full h-32 flex items-center justify-center text-sm text-gray-500">Failed to load image</div>';
                            }
                          }}
                        />

                        {/* Selection Overlay */}
                        {isSelected && (
                          <div className="absolute inset-0 bg-blue-500 bg-opacity-20 flex items-center justify-center">
                            <Check className="h-8 w-8 text-white drop-shadow-md" />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        }

        // Check if any options have option_media
        const hasOptionMedia = currentQuestion.option_media && Object.keys(currentQuestion.option_media).length > 0;
        const allOptionMediaLoaded = !hasOptionMedia || optionMediaTimedOut || loadedOptionMediaCount >= totalOptionMedia;

        // If we have option_media AND media_urls, render media_urls as question-level media
        const questionLevelMedia = hasOptionMedia && hasMediaChoice ? orderedQuestionMediaUrls : null;

        return (
          <div className="space-y-3">
            {/* Render question-level media if present */}
            {questionLevelMedia && questionLevelMedia.length > 0 && (
              <div className="mb-6">
                <div className="grid gap-4 grid-cols-1 max-w-3xl mx-auto">
                  {questionLevelMedia.map((url: string, mediaIdx: number) => {
                    const isVideo = isVideoUrl(url);
                    return (
                      <div key={`q-media-${mediaIdx}`} className="border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                        {isVideo ? (
                          <video
                            src={getFullMediaUrl(url)}
                            className="w-full h-auto max-h-[400px]"
                            controls
                          />
                        ) : (
                          <img
                            src={getFullMediaUrl(url)}
                            alt={`Question media ${mediaIdx + 1}`}
                            className="w-full h-auto max-h-[400px] object-contain"
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {randomizedChoices.map((choice: string, index: number) => {
              const isSelected = currentResponse === choice;
              // Get the original index for option_media lookup (before randomization)
              const originalIndex = currentQuestion.choices?.indexOf(choice) ?? index;
              const optionMediaUrls = currentQuestion.option_media?.[String(originalIndex)] || currentQuestion.option_media?.[originalIndex] || [];
              
              // Render differently based on whether option has media
              if (optionMediaUrls.length > 0) {
                // Card-style layout for options with media
                return (
                  <button
                    key={choice}
                    type="button"
                    disabled={hasOptionMedia && !allOptionMediaLoaded}
                    onClick={async () => {
                      if (hasOptionMedia && !allOptionMediaLoaded) return;
                      handleResponseChange(choice);
                      handleSubmitResponse(choice);
                    }}
                    className={`w-full text-left rounded-xl border-2 transition-all duration-200 overflow-hidden ${
                      hasOptionMedia && !allOptionMediaLoaded
                        ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                        : isSelected
                          ? 'border-blue-500 bg-blue-50 shadow-md ring-2 ring-blue-200'
                          : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'
                    }`}
                  >
                    {/* Option header with choice text */}
                    <div className={`px-4 py-3 flex items-center justify-between ${
                      isSelected ? 'bg-blue-100' : 'bg-gray-50'
                    }`}>
                      <span className={`font-medium ${isSelected ? 'text-blue-900' : 'text-gray-900'}`}>
                        {choice}
                      </span>
                      {isSelected && (
                        <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                          <Check className="w-4 h-4 text-white" />
                        </div>
                      )}
                    </div>
                    
                    {/* Media grid */}
                    <div className="p-3">
                      <div className={`grid gap-2 ${
                        optionMediaUrls.length === 1 
                          ? 'grid-cols-1' 
                          : optionMediaUrls.length === 2 
                            ? 'grid-cols-2' 
                            : 'grid-cols-3'
                      }`}>
                        {optionMediaUrls.map((url: string, mediaIndex: number) => {
                          const mediaKey = `${originalIndex}-${mediaIndex}`;
                          const isLoaded = optionMediaLoaded[mediaKey];
                          
                          return (
                            <div
                              key={mediaIndex}
                              className={OPTION_MEDIA_CONTAINER_CLASS}
                            >
                              {!isLoaded && (
                                <div className="absolute inset-0 flex items-center justify-center bg-gray-100 animate-pulse">
                                  <div className="w-6 h-6 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                                </div>
                              )}
                              <img
                                src={getFullMediaUrl(url)}
                                alt={`${choice} media ${mediaIndex + 1}`}
                                className={`${OPTION_MEDIA_IMG_CLASS} transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
                                ref={(el) => { if (el?.complete) markOptionMediaLoaded(mediaKey); }}
                                onLoad={() => markOptionMediaLoaded(mediaKey) }
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = 'none';
                                  markOptionMediaLoaded(mediaKey);
                                }}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </button>
                );
              }
              
              // Standard layout for options without media
              return (
                <button
                  key={choice}
                  type="button"
                  onClick={async () => {
                    handleResponseChange(choice);
                    handleSubmitResponse(choice);
                  }}
                  className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all duration-200 ${
                    isSelected
                      ? 'border-blue-500 bg-blue-50 text-blue-900'
                      : 'border-gray-300 bg-white text-gray-900 hover:border-gray-400 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span>{choice}</span>
                    {isSelected && (
                      <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
            
            {/* Other option if enabled */}
            {currentQuestion.allow_other && (
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => {
                    handleResponseChange({ choice: 'Other', other_text: '' });
                  }}
                  className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all duration-200 ${
                    typeof currentResponse === 'object' && currentResponse?.choice === 'Other'
                      ? 'border-blue-500 bg-blue-50 text-blue-900'
                      : 'border-gray-300 bg-white text-gray-900 hover:border-gray-400 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span>{UI_TEXT.AI_SURVEY.SESSION.CHOICES.OTHER}</span>
                    {typeof currentResponse === 'object' && currentResponse?.choice === 'Other' && (
                      <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </div>
                </button>
                
                {/* Text input for Other option */}
                {typeof currentResponse === 'object' && currentResponse?.choice === 'Other' && (
                  <input
                    type="text"
                    placeholder={UI_TEXT.AI_SURVEY.SESSION.PLACEHOLDERS.OTHER_SPECIFY}
                    value={currentResponse?.other_text || ''}
                    onChange={(e) => {
                      handleResponseChange({
                        choice: 'Other',
                        other_text: e.target.value
                      });
                    }}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    autoFocus
                  />
                )}
              </div>
            )}
          </div>
        );
      
      case 'multiple_choice':
        // Check if this is a media question and determine display mode
        const hasMediaMultiple = currentQuestion.media_urls && currentQuestion.media_urls.length > 0;
        // Only use choice-level mode if EXPLICITLY set to 'choices' (legacy surveys)
        // New system: media_urls = question-level, option_media = per-option
        const displayModeMultiple = currentQuestion.media_display_mode || 'question'; // Default to question-level for new system
        const isQuestionModeMultiple = displayModeMultiple === 'question';

        // Question-level media mode (media displayed below question text)
        if (hasMediaMultiple && isQuestionModeMultiple) {
          return (
            <div className="space-y-4">
              {/* Question-level media — info-screen-style responsive grid. Wires the stimulus
                  gate so choices stay locked until the images paint. */}
              {renderSimpleQuestionMedia()}

              {/* Choices — with option_media if present */}
              {/* Selection is locked until the stimulus above has actually painted, so the
                  respondent cannot tick options for an image they never saw. These buttons only
                  TOGGLE (unlike single_choice, which submits on click) — the submit is the Next
                  button, which is gated on stimulusReady too. <fieldset disabled> also blocks
                  keyboard activation, which a pointer-events rule would not. Fails open. */}
              <fieldset
                disabled={!stimulusReady}
                aria-busy={!stimulusReady}
                className={`space-y-3 border-0 p-0 m-0 transition-opacity duration-200 ${
                  stimulusReady ? 'opacity-100' : 'opacity-40 cursor-progress'
                }`}
              >
                {!stimulusReady && (
                  <p className="text-sm text-gray-500 italic" role="status">
                    {UI_TEXT.AI_SURVEY.SESSION.LOADING_STIMULUS}
                  </p>
                )}
                {randomizedChoices.map((choice: string, index: number) => {
                  const responses = Array.isArray(currentResponse) ? currentResponse : [];
                  const isSelected = responses.some((item: any) =>
                    typeof item === 'string' ? item === choice : item?.choice === choice
                  );
                  const origIdx = currentQuestion.choices?.indexOf(choice) ?? index;
                  const choiceMedia = currentQuestion.option_media?.[String(origIdx)] || currentQuestion.option_media?.[origIdx] || [];

                  const toggleChoice = () => {
                    const currentResponses = Array.isArray(currentResponse) ? currentResponse : [];
                    if (isSelected) {
                      handleResponseChange(currentResponses.filter((item: any) =>
                        typeof item === 'string' ? item !== choice : item?.choice !== choice
                      ));
                    } else {
                      handleResponseChange([...currentResponses, choice]);
                    }
                  };

                  if (choiceMedia.length > 0) {
                    return (
                      <button
                        key={choice}
                        type="button"
                        onClick={toggleChoice}
                        className={`w-full text-left rounded-xl border-2 transition-all duration-200 overflow-hidden ${
                          isSelected
                            ? 'border-blue-500 bg-blue-50 shadow-md ring-2 ring-blue-200'
                            : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'
                        }`}
                      >
                        <div className={`px-4 py-3 flex items-center justify-between ${isSelected ? 'bg-blue-100' : 'bg-gray-50'}`}>
                          <span className={`font-medium ${isSelected ? 'text-blue-900' : 'text-gray-900'}`}>{choice}</span>
                          {isSelected && (
                            <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                              <Check className="w-4 h-4 text-white" />
                            </div>
                          )}
                        </div>
                        <div className="p-3">
                          <div className={`grid gap-2 ${choiceMedia.length === 1 ? 'grid-cols-1' : choiceMedia.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
                            {choiceMedia.map((url: string, mediaIdx: number) => (
                              <div key={mediaIdx} className={OPTION_MEDIA_CONTAINER_CLASS}>
                                <img
                                  src={getFullMediaUrl(url)}
                                  alt={`${choice} media ${mediaIdx + 1}`}
                                  className={OPTION_MEDIA_IMG_CLASS}
                                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      </button>
                    );
                  }

                  return (
                    <button
                      key={choice}
                      type="button"
                      onClick={toggleChoice}
                      className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all duration-200 ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50 text-blue-900'
                          : 'border-gray-300 bg-white text-gray-900 hover:border-gray-400 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span>{choice}</span>
                        {isSelected && (
                          <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                            <Check className="w-3 h-3 text-white" />
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}

                {/* Other option if enabled */}
                {currentQuestion.allow_other && (
                  <div className="space-y-2">
                    <button
                      type="button"
                      onClick={() => {
                        const responses = Array.isArray(currentResponse) ? currentResponse : [];
                        const otherExists = responses.some((item: any) =>
                          typeof item === 'object' && item?.choice === 'Other'
                        );

                        if (otherExists) {
                          // Remove Other option
                          handleResponseChange(responses.filter((item: any) =>
                            !(typeof item === 'object' && item?.choice === 'Other')
                          ));
                        } else {
                          // Add Other option
                          handleResponseChange([...responses, { choice: 'Other', other_text: '' }]);
                        }
                      }}
                      className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all duration-200 ${
                        Array.isArray(currentResponse) && currentResponse.some((item: any) =>
                          typeof item === 'object' && item?.choice === 'Other'
                        )
                          ? 'border-blue-500 bg-blue-50 text-blue-900'
                          : 'border-gray-300 bg-white text-gray-900 hover:border-gray-400 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span>{UI_TEXT.AI_SURVEY.SESSION.CHOICES.OTHER}</span>
                        {Array.isArray(currentResponse) && currentResponse.some((item: any) =>
                          typeof item === 'object' && item?.choice === 'Other'
                        ) && (
                          <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                            <Check className="w-3 h-3 text-white" />
                          </div>
                        )}
                      </div>
                    </button>

                    {/* Text input for Other option */}
                    {Array.isArray(currentResponse) && currentResponse.some((item: any) =>
                      typeof item === 'object' && item?.choice === 'Other'
                    ) && (
                      <input
                        type="text"
                        placeholder={UI_TEXT.AI_SURVEY.SESSION.PLACEHOLDERS.OTHER_SPECIFY}
                        value={(() => {
                          const otherItem = currentResponse.find((item: any) =>
                            typeof item === 'object' && item?.choice === 'Other'
                          );
                          return otherItem?.other_text || '';
                        })()}
                        onChange={(e) => {
                          const responses = Array.isArray(currentResponse) ? currentResponse : [];
                          const updatedResponses = responses.map((item: any) =>
                            typeof item === 'object' && item?.choice === 'Other'
                              ? { choice: 'Other', other_text: e.target.value }
                              : item
                          );
                          handleResponseChange(updatedResponses);
                        }}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        autoFocus
                      />
                    )}
                  </div>
                )}
              </fieldset>
            </div>
          );
        }

        // Choice-level media mode (media displayed with each choice) - existing/legacy behavior
        // Only use this if option_media is NOT present (option_media is the new preferred approach)
        const hasNewOptionMediaMultiple = currentQuestion.option_media && Object.keys(currentQuestion.option_media).length > 0;
        if (hasMediaMultiple && !isQuestionModeMultiple && !hasNewOptionMediaMultiple) {
          const displayUrls = orderedQuestionMediaUrls;
          const storageUrls = currentQuestion.media_urls || [];
          const selectedIndices = currentResponse?.selected_indices || [];

          return (
            <div className="space-y-4">
              {/* Instructions */}
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  You can select multiple images by clicking on them.
                </p>
                <span className="inline-flex items-center rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600">
                  Select multiple
                </span>
              </div>

              {/* Image Grid */}
              <div className={`grid gap-6 ${
                displayUrls.length === 1
                  ? 'grid-cols-1 max-w-2xl mx-auto'
                  : displayUrls.length === 2
                  ? 'grid-cols-1 lg:grid-cols-2 max-w-4xl mx-auto'
                  : 'grid-cols-1 lg:grid-cols-2 max-w-5xl mx-auto'
              }`}>
                {displayUrls.map((url: string, displayIdx: number) => {
                  const originalIdx = questionMediaOriginalIndices[displayIdx];
                  const isSelected = selectedIndices.includes(originalIdx);

                  return (
                    <div
                      key={`${currentQuestion.id}-image-${originalIdx}`}
                      className={`cursor-pointer transition-all duration-200 hover:shadow-lg border-2 rounded-lg p-4 ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50 shadow-md'
                          : 'border-gray-300 hover:border-gray-400 hover:shadow-md'
                      }`}
                      onClick={() => {
                        let newSelectedIndices: number[];
                        if (isSelected) {
                          newSelectedIndices = selectedIndices.filter((i: number) => i !== originalIdx);
                        } else {
                          newSelectedIndices = [...selectedIndices, originalIdx];
                        }

                        const response = {
                          selected_indices: newSelectedIndices,
                          selected_urls: newSelectedIndices.map((i: number) => storageUrls[i])
                        };
                        handleResponseChange(response);
                      }}
                    >
                      {/* Selection Indicator */}
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-gray-700">
                          {currentQuestion.choices?.[originalIdx] || `Image ${originalIdx + 1}`}
                        </span>
                        {isSelected && (
                          <Check className="h-5 w-5 text-blue-500" />
                        )}
                      </div>

                      {/* Image Container */}
                      <div className="relative rounded-md overflow-hidden">
                        <img
                          src={getFullMediaUrl(url)}
                          alt={`Image ${originalIdx + 1} - Choice option`}
                          className="w-full h-auto max-h-80 object-contain rounded-md"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            const parent = target.parentElement;
                            if (parent) {
                              parent.innerHTML = '<div class="w-full h-32 flex items-center justify-center text-sm text-gray-500">Failed to load image</div>';
                            }
                          }}
                        />

                        {/* Selection Overlay */}
                        {isSelected && (
                          <div className="absolute inset-0 bg-blue-500 bg-opacity-20 flex items-center justify-center">
                            <Check className="h-8 w-8 text-white drop-shadow-md" />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        }

        // Check if any options have option_media for multiple choice
        const hasOptionMediaMultiple = currentQuestion.option_media && Object.keys(currentQuestion.option_media).length > 0;
        const allOptionMediaLoadedMultiple = !hasOptionMediaMultiple || optionMediaTimedOut || loadedOptionMediaCount >= totalOptionMedia;

        // If we have option_media AND media_urls, render media_urls as question-level media
        const questionLevelMediaMultiple = hasOptionMediaMultiple && hasMediaMultiple ? orderedQuestionMediaUrls : null;

        return (
          <div className="space-y-3">
            {/* Render question-level media if present */}
            {questionLevelMediaMultiple && questionLevelMediaMultiple.length > 0 && (
              <div className="mb-6">
                <div className="grid gap-4 grid-cols-1 max-w-3xl mx-auto">
                  {questionLevelMediaMultiple.map((url: string, mediaIdx: number) => {
                    const isVideo = isVideoUrl(url);
                    return (
                      <div key={`q-media-${mediaIdx}`} className="border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                        {isVideo ? (
                          <video
                            src={getFullMediaUrl(url)}
                            className="w-full h-auto max-h-[400px]"
                            controls
                          />
                        ) : (
                          <img
                            src={getFullMediaUrl(url)}
                            alt={`Question media ${mediaIdx + 1}`}
                            className="w-full h-auto max-h-[400px] object-contain"
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {randomizedChoices.map((choice: string, index: number) => {
              const responses = Array.isArray(currentResponse) ? currentResponse : [];
              const isSelected = responses.some((item: any) =>
                typeof item === 'string' ? item === choice : item?.choice === choice
              );
              // Get the original index for option_media lookup (before randomization)
              const originalIndexMultiple = currentQuestion.choices?.indexOf(choice) ?? index;
              const optionMediaUrlsMultiple = currentQuestion.option_media?.[String(originalIndexMultiple)] || currentQuestion.option_media?.[originalIndexMultiple] || [];
              
              // Render card-style for options with media
              if (optionMediaUrlsMultiple.length > 0) {
                return (
                  <button
                    key={choice}
                    type="button"
                    disabled={hasOptionMediaMultiple && !allOptionMediaLoadedMultiple}
                    onClick={() => {
                      if (hasOptionMediaMultiple && !allOptionMediaLoadedMultiple) return;
                      const currentResponses = Array.isArray(currentResponse) ? currentResponse : [];
                      if (isSelected) {
                        handleResponseChange(currentResponses.filter((item: any) =>
                          typeof item === 'string' ? item !== choice : item?.choice !== choice
                        ));
                      } else {
                        handleResponseChange([...currentResponses, choice]);
                      }
                    }}
                    className={`w-full text-left rounded-xl border-2 transition-all duration-200 overflow-hidden ${
                      hasOptionMediaMultiple && !allOptionMediaLoadedMultiple
                        ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                        : isSelected
                          ? 'border-blue-500 bg-blue-50 shadow-md ring-2 ring-blue-200'
                          : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'
                    }`}
                  >
                    {/* Option header with choice text */}
                    <div className={`px-4 py-3 flex items-center justify-between ${
                      isSelected ? 'bg-blue-100' : 'bg-gray-50'
                    }`}>
                      <span className={`font-medium ${isSelected ? 'text-blue-900' : 'text-gray-900'}`}>
                        {choice}
                      </span>
                      <div className={`w-6 h-6 rounded border-2 flex items-center justify-center ${
                        isSelected ? 'bg-blue-500 border-blue-500' : 'border-gray-300 bg-white'
                      }`}>
                        {isSelected && <Check className="w-4 h-4 text-white" />}
                      </div>
                    </div>
                    
                    {/* Media grid */}
                    <div className="p-3">
                      <div className={`grid gap-2 ${
                        optionMediaUrlsMultiple.length === 1 
                          ? 'grid-cols-1' 
                          : optionMediaUrlsMultiple.length === 2 
                            ? 'grid-cols-2' 
                            : 'grid-cols-3'
                      }`}>
                        {optionMediaUrlsMultiple.map((url: string, mediaIndex: number) => {
                          const mediaKey = `${originalIndexMultiple}-${mediaIndex}`;
                          const isLoaded = optionMediaLoaded[mediaKey];
                          
                          return (
                            <div
                              key={mediaIndex}
                              className={OPTION_MEDIA_CONTAINER_CLASS}
                            >
                              {!isLoaded && (
                                <div className="absolute inset-0 flex items-center justify-center bg-gray-100 animate-pulse">
                                  <div className="w-6 h-6 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                                </div>
                              )}
                              <img
                                src={getFullMediaUrl(url)}
                                alt={`${choice} media ${mediaIndex + 1}`}
                                className={`${OPTION_MEDIA_IMG_CLASS} transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
                                ref={(el) => { if (el?.complete) markOptionMediaLoaded(mediaKey); }}
                                onLoad={() => markOptionMediaLoaded(mediaKey)}
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = 'none';
                                  markOptionMediaLoaded(mediaKey);
                                }}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </button>
                );
              }
              
              // Standard layout for options without media
              return (
                <button
                  key={choice}
                  type="button"
                  onClick={() => {
                    const currentResponses = Array.isArray(currentResponse) ? currentResponse : [];
                    if (isSelected) {
                      handleResponseChange(currentResponses.filter((item: any) =>
                        typeof item === 'string' ? item !== choice : item?.choice !== choice
                      ));
                    } else {
                      handleResponseChange([...currentResponses, choice]);
                    }
                  }}
                  className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all duration-200 ${
                    isSelected
                      ? 'border-blue-500 bg-blue-50 text-blue-900'
                      : 'border-gray-300 bg-white text-gray-900 hover:border-gray-400 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span>{choice}</span>
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                      isSelected ? 'bg-blue-500 border-blue-500' : 'border-gray-300 bg-white'
                    }`}>
                      {isSelected && <Check className="w-3 h-3 text-white" />}
                    </div>
                  </div>
                </button>
              );
            })}
            
            {/* Other option if enabled */}
            {currentQuestion.allow_other && (
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => {
                    const responses = Array.isArray(currentResponse) ? currentResponse : [];
                    const otherExists = responses.some((item: any) => 
                      typeof item === 'object' && item?.choice === 'Other'
                    );
                    
                    if (otherExists) {
                      // Remove Other option
                      handleResponseChange(responses.filter((item: any) => 
                        !(typeof item === 'object' && item?.choice === 'Other')
                      ));
                    } else {
                      // Add Other option
                      handleResponseChange([...responses, { choice: 'Other', other_text: '' }]);
                    }
                  }}
                  className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all duration-200 ${
                    Array.isArray(currentResponse) && currentResponse.some((item: any) => 
                      typeof item === 'object' && item?.choice === 'Other'
                    )
                      ? 'border-blue-500 bg-blue-50 text-blue-900'
                      : 'border-gray-300 bg-white text-gray-900 hover:border-gray-400 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span>{UI_TEXT.AI_SURVEY.SESSION.CHOICES.OTHER}</span>
                    {Array.isArray(currentResponse) && currentResponse.some((item: any) => 
                      typeof item === 'object' && item?.choice === 'Other'
                    ) && (
                      <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </div>
                </button>
                
                {/* Text input for Other option */}
                {Array.isArray(currentResponse) && currentResponse.some((item: any) => 
                  typeof item === 'object' && item?.choice === 'Other'
                ) && (
                  <input
                    type="text"
                    placeholder={UI_TEXT.AI_SURVEY.SESSION.PLACEHOLDERS.OTHER_SPECIFY}
                    value={(() => {
                      const otherItem = currentResponse.find((item: any) => 
                        typeof item === 'object' && item?.choice === 'Other'
                      );
                      return otherItem?.other_text || '';
                    })()}
                    onChange={(e) => {
                      const responses = Array.isArray(currentResponse) ? currentResponse : [];
                      const updatedResponses = responses.map((item: any) => 
                        typeof item === 'object' && item?.choice === 'Other'
                          ? { choice: 'Other', other_text: e.target.value }
                          : item
                      );
                      handleResponseChange(updatedResponses);
                    }}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    autoFocus
                  />
                )}
              </div>
            )}
          </div>
        );

      case 'yes_no':
        return (
          <div className="space-y-3">
            {renderSimpleQuestionMedia()}
            {(currentQuestion.choices || ['Yes', 'No']).map((choice: string, index: number) => {
              const isSelected = currentResponse === choice;
              return (
                <button
                  key={index}
                  type="button"
                  onClick={async () => {
                    handleResponseChange(choice);
                    handleSubmitResponse(choice);
                  }}
                  className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all duration-200 ${
                    isSelected
                      ? 'border-blue-500 bg-blue-50 text-blue-900'
                      : 'border-gray-300 bg-white text-gray-900 hover:border-gray-400 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span>{choice}</span>
                    {isSelected && (
                      <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        );

      case 'rating_scale':
        return (
          <div className="space-y-4">
            {renderSimpleQuestionMedia()}
            <div className="text-center">
              <div className="flex justify-center space-x-2">
                {Array.from({ length: (currentQuestion.scale_max || 5) - (currentQuestion.scale_min || 1) + 1 }, (_, index) => {
                  const value = (currentQuestion.scale_min || 1) + index;
                  const isSelected = currentResponse === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => {
                        handleResponseChange(value);
                        handleSubmitResponse(value);
                      }}
                      className={`w-12 h-12 rounded-full border-2 font-semibold transition-all duration-200 ${
                        isSelected
                          ? 'border-blue-500 bg-blue-500 text-white'
                          : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400 hover:bg-gray-50'
                      }`}
                    >
                      {value}
                    </button>
                  );
                })}
              </div>

              {/* Scale labels */}
              <div className="flex items-center justify-between text-xs text-gray-500 mt-3 max-w-xs mx-auto">
                <span className="text-left">Not at all</span>
                <span className="text-right">Extremely</span>
              </div>
            </div>
          </div>
        );

      case 'likert_scale':
        return (
          <div className="space-y-3">
            {renderSimpleQuestionMedia()}
            {[
              'Strongly Disagree',
              'Disagree',
              'Neutral',
              'Agree',
              'Strongly Agree'
            ].map((choice: string, index: number) => {
              const isSelected = currentResponse === choice;
              return (
                <button
                  key={index}
                  type="button"
                  onClick={async () => {
                    handleResponseChange(choice);
                    handleSubmitResponse(choice);
                  }}
                  className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all duration-200 ${
                    isSelected
                      ? 'border-blue-500 bg-blue-50 text-blue-900'
                      : 'border-gray-300 bg-white text-gray-900 hover:border-gray-400 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span>{choice}</span>
                    {isSelected && (
                      <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        );

      case 'dropdown':
        return (
          <div className="space-y-4">
            {renderSimpleQuestionMedia()}
            <select
              value={currentResponse || ''}
              onChange={(e) => {
                handleResponseChange(e.target.value);
                handleSubmitResponse(e.target.value);
              }}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              autoFocus
            >
              <option value="">Select an option...</option>
              {currentQuestion.choices?.map((choice: string, index: number) => (
                <option key={index} value={choice}>
                  {choice}
                </option>
              ))}
            </select>
          </div>
        );

      case 'nps':
        return (
          <div className="space-y-4">
            {renderSimpleQuestionMedia()}
            <div className="text-center">
              <div className="text-sm text-gray-600 mb-4">
                On a scale of 0-10, how likely are you to recommend this to a friend or colleague?
              </div>
              <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
                <span>Not at all likely</span>
                <span>Extremely likely</span>
              </div>
              <div className="flex justify-center space-x-1">
                {Array.from({ length: 11 }, (_, index) => {
                  const value = index;
                  const isSelected = currentResponse === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => {
                        handleResponseChange(value);
                        handleSubmitResponse(value);
                      }}
                      className={`w-10 h-10 rounded border-2 font-semibold text-sm transition-all duration-200 ${
                        isSelected
                          ? 'border-blue-500 bg-blue-500 text-white'
                          : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400 hover:bg-gray-50'
                      }`}
                    >
                      {value}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        );

      case 'numeric_input':
        return (
          <div className="space-y-4">
            {renderSimpleQuestionMedia()}
            <input
              type="number"
              value={currentResponse || ''}
              onChange={(e) => handleResponseChange(e.target.value)}
              placeholder="Enter a number..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              autoFocus
            />
          </div>
        );

      case 'ranking':
        return (
          <div className="space-y-4">
            {renderSimpleQuestionMedia()}
            <div className="text-sm text-gray-600 mb-4">
              Click to select, then drag to reorder your top {currentQuestion.ranking_count || 3} choices:
            </div>

            {/* Ranked items (draggable) */}
            <div className="mb-4">
              <div className="text-sm font-medium text-gray-700 mb-2">Your Rankings:</div>
              <div className="space-y-2 min-h-[60px] p-3 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
                {Array.isArray(currentResponse) && currentResponse.length > 0 ? (
                  currentResponse.map((choice: string, index: number) => (
                    <div
                      key={choice}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData('text/plain', index.toString());
                        e.currentTarget.style.opacity = '0.5';
                      }}
                      onDragEnd={(e) => {
                        e.currentTarget.style.opacity = '1';
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        const draggedIndex = parseInt(e.dataTransfer.getData('text/plain'));
                        const droppedIndex = index;

                        if (draggedIndex !== droppedIndex) {
                          const newRankings = Array.isArray(currentResponse) ? [...currentResponse] : [];
                          const draggedItem = newRankings[draggedIndex];
                          newRankings.splice(draggedIndex, 1);
                          newRankings.splice(droppedIndex, 0, draggedItem);
                          handleResponseChange(newRankings);
                        }
                      }}
                      className="flex items-center justify-between px-4 py-3 bg-blue-50 border-2 border-blue-200 rounded-lg cursor-move hover:bg-blue-100 transition-colors"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                          {index + 1}
                        </div>
                        <span className="text-blue-900 font-medium">{choice}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"/>
                        </svg>
                        <button
                          type="button"
                          onClick={() => {
                            const newRankings = Array.isArray(currentResponse) ? [...currentResponse] : [];
                            newRankings.splice(index, 1);
                            handleResponseChange(newRankings);
                          }}
                          className="text-red-500 hover:text-red-700 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center text-gray-500 py-4">
                    Select items below to start ranking
                  </div>
                )}
              </div>
            </div>

            {/* Available choices */}
            <div>
              <div className="text-sm font-medium text-gray-700 mb-2">Available Choices:</div>
              <div className="space-y-2">
                {currentQuestion.choices?.map((choice: string, index: number) => {
                  const rankings = Array.isArray(currentResponse) ? currentResponse : [];
                  const isRanked = rankings.includes(choice);
                  const maxRank = currentQuestion.ranking_count || 3;
                  const canAdd = rankings.length < maxRank;

                  if (isRanked) return null; // Don't show already ranked items

                  return (
                    <button
                      key={index}
                      type="button"
                      onClick={() => {
                        if (canAdd) {
                          const newRankings = Array.isArray(currentResponse) ? [...currentResponse] : [];
                          newRankings.push(choice);
                          handleResponseChange(newRankings);
                        }
                      }}
                      disabled={!canAdd}
                      className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all duration-200 ${
                        canAdd
                          ? 'border-gray-300 bg-white text-gray-900 hover:border-gray-400 hover:bg-gray-50 cursor-pointer'
                          : 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span>{choice}</span>
                        {canAdd && (
                          <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                          </svg>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="text-xs text-gray-500 mt-2">
              Selected: {Array.isArray(currentResponse) ? currentResponse.length : 0} of {currentQuestion.ranking_count || 3}
            </div>
          </div>
        );

      case 'matrix_rating':
        // Use randomized rows if randomization is enabled, otherwise use original order
        const matrixRows = randomizedMatrixRows.length > 0 ? randomizedMatrixRows : (currentQuestion.matrix_rows || currentQuestion.choices || []);

        return (
          <div className="space-y-4">
            {renderSimpleQuestionMedia()}
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="text-left p-3 border border-gray-300 font-medium text-gray-700 min-w-[200px]">
                      {/* Empty header for row labels */}
                    </th>
                    {(currentQuestion.matrix_columns || ['1', '2', '3', '4', '5']).map((label, index) => (
                      <th key={index} className="text-center p-3 border border-gray-300 font-medium text-gray-700 min-w-[80px]">
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {matrixRows.map((item, itemIndex) => (
                    <tr key={itemIndex} className="hover:bg-gray-50">
                      <td className="p-3 border border-gray-300 font-medium text-gray-900">
                        {item}
                      </td>
                      {(currentQuestion.matrix_columns || ['1', '2', '3', '4', '5']).map((label, scaleIndex) => {
                        const scaleValue = currentQuestion.scale_min ? currentQuestion.scale_min + scaleIndex : scaleIndex + 1;
                        const matrixResponse = currentResponse || {};
                        const isSelected = matrixResponse[item] === scaleValue;

                        return (
                          <td key={scaleIndex} className="p-3 border border-gray-300 text-center">
                            <input
                              type="radio"
                              name={`matrix_${itemIndex}`}
                              value={scaleValue}
                              checked={isSelected}
                              onChange={() => {
                                const newResponse = { ...matrixResponse };
                                newResponse[item] = scaleValue;
                                handleResponseChange(newResponse);
                              }}
                              className="w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'max_diff':
        // Generate balanced sets using user-seeded randomization
        const maxDiffSets = session ? generateAndPrepareSetsForUser(currentQuestion.choices || [], session.id.toString(), 4, 4) : [];

        // Evaluate and log the design quality for this session
        if (session && maxDiffSets.length > 0) {
          const evaluation = evaluateMaxDiffDesign(maxDiffSets, currentQuestion.choices || []);
          console.log(`[MaxDiff Evaluation - Session ${session.id} - Render]`, evaluation);
        }

        const currentSet = maxDiffSets[currentMaxDiffSetIndex] || [];
        const totalSets = maxDiffSets.length;

        return (
          <div className="space-y-6">
            {renderSimpleQuestionMedia()}
            <div className="text-center mb-6">
              <div className="text-sm text-gray-500 mb-2">
                Set {currentMaxDiffSetIndex + 1} of {totalSets}
              </div>
              <div className="text-xs text-gray-600">
                Select the most and least appealing options • No right or wrong answers
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Most Appealing Section */}
              <div className="border-2 border-green-200 rounded-lg p-4 bg-green-50">
                <h3 className="text-lg font-semibold text-green-800 mb-3 text-center">Most Appealing</h3>
                <div className="space-y-2">
                  {currentSet.map((choice, index) => {
                    const response = currentResponse || {};
                    const isSelected = response.most_appealing === choice;
                    const isDisabled = response.least_appealing === choice;

                    return (
                      <button
                        key={index}
                        type="button"
                        disabled={isDisabled}
                        onClick={() => {
                          const newResponse = { ...response };
                          newResponse.most_appealing = choice;
                          handleResponseChange(newResponse);
                        }}
                        className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all duration-200 ${
                          isSelected
                            ? 'border-green-500 bg-green-100 text-green-900'
                            : isDisabled
                            ? 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'border-gray-300 bg-white text-gray-900 hover:border-green-400 hover:bg-green-50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span>{choice}</span>
                          {isSelected && (
                            <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                              <Check className="w-3 h-3 text-white" />
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Least Appealing Section */}
              <div className="border-2 border-red-200 rounded-lg p-4 bg-red-50">
                <h3 className="text-lg font-semibold text-red-800 mb-3 text-center">Least Appealing</h3>
                <div className="space-y-2">
                  {currentSet.map((choice, index) => {
                    const response = currentResponse || {};
                    const isSelected = response.least_appealing === choice;
                    const isDisabled = response.most_appealing === choice;

                    return (
                      <button
                        key={index}
                        type="button"
                        disabled={isDisabled}
                        onClick={() => {
                          const newResponse = { ...response };
                          newResponse.least_appealing = choice;
                          handleResponseChange(newResponse);
                        }}
                        className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all duration-200 ${
                          isSelected
                            ? 'border-red-500 bg-red-100 text-red-900'
                            : isDisabled
                            ? 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'border-gray-300 bg-white text-gray-900 hover:border-red-400 hover:bg-red-50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span>{choice}</span>
                          {isSelected && (
                            <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
                              <Check className="w-3 h-3 text-white" />
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        );

      case 'conjoint_analysis':
        return (
          <div className="space-y-6">
            {renderSimpleQuestionMedia()}
            {(() => {
              const attributes = currentQuestion.conjoint_attributes || [];

              if (attributes.length === 0) {
                return (
                  <div className="text-center text-gray-500 py-8">
                    No attributes configured for this conjoint analysis question.
                  </div>
                );
              }

              // Generate choice task (3 profiles per task is optimal). Shared helper so the
              // profiles shown here match the profile_details recovered on submit.
              const profilesPerTask = 3;
              const allProfiles = generateConjointProfiles(attributes);

              // Get current task index from state
              const currentTaskIndex = currentConjointScenarioIndex;

              // Select profiles for current choice task using systematic rotation
              const taskProfiles = [];
              for (let i = 0; i < profilesPerTask; i++) {
                const profileIndex = (currentTaskIndex * profilesPerTask + i) % allProfiles.length;
                taskProfiles.push(allProfiles[profileIndex]);
              }

              return (
                <div className="space-y-4">
                  {/* Choice task header */}
                  <div className="text-center">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Which option would you be most likely to choose?
                    </h3>
                    <p className="text-sm text-gray-600">
                      Select the one option that best meets your preferences
                    </p>
                    <div className="text-sm text-gray-500 mt-2">
                      Scenario {currentConjointScenarioIndex + 1} of {currentQuestion.conjoint_scenarios || Math.min(12, Math.max(8, attributes.length * 2))}
                    </div>
                  </div>

                  {/* Profile options */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {taskProfiles.map((profile, index) => {
                      const isSelected = currentResponse === profile.id;

                      return (
                        <div
                          key={profile.id}
                          className={`border-2 rounded-lg p-4 cursor-pointer transition-all duration-200 ${
                            isSelected
                              ? 'border-blue-500 bg-blue-50 shadow-md'
                              : 'border-gray-300 bg-white hover:border-blue-300 hover:shadow-sm'
                          }`}
                          onClick={() => handleResponseChange(profile.id)}
                        >
                          <div className="text-center mb-4">
                            <h4 className="font-semibold text-gray-900 text-lg">
                              Option {String.fromCharCode(65 + index)}
                            </h4>
                          </div>

                          <div className="space-y-3">
                            {attributes.map(attr => (
                              <div key={attr.name} className="border-b border-gray-100 pb-2 last:border-b-0">
                                <div className="flex flex-col space-y-1">
                                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                    {attr.name.replace(/_/g, ' ')}
                                  </span>
                                  <span className="text-sm font-semibold text-gray-900">
                                    {String(profile.attributes[attr.name]).replace(/_/g, ' ')}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>

                          {isSelected && (
                            <div className="mt-4 flex justify-center">
                              <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                                <Check className="w-5 h-5 text-white" />
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </div>
        );

      case 'image_hotspot':
        // Build public image URL from question config
        const hotspotImageUrl = currentQuestion.image_url
          ? (currentQuestion.image_url.startsWith('http')
              ? currentQuestion.image_url
              : `${import.meta.env.VITE_API_URL || ''}/api/question-images/public/${survey.id}/${currentQuestion.image_url.split('/').pop()}`)
          : '';

        return (
          <div className="space-y-4">
            <ImageHotspotResponse
              imageUrl={hotspotImageUrl}
              boundingBoxes={currentQuestion.bounding_boxes || []}
              rankingCount={currentQuestion.ranking_count || 3}
              freeClickMode={currentQuestion.free_click_mode || false}
              fitToScreen={currentQuestion.fit_to_screen !== false}
              customSize={currentQuestion.custom_size || false}
              customWidthVw={currentQuestion.custom_width_vw}
              customHeightVh={currentQuestion.custom_height_vh}
              onComplete={(clicks) => {
                // Format response data for submission
                const responseData = { clicks };
                handleResponseChange(responseData);
              }}
              disabled={isSubmitting}
            />
          </div>
        );

      case 'info_screen':
        // Information display - no response needed, user just clicks continue
        // Use key to force remount when question changes, ensuring clean loading states
        return (
          <InfoScreenDisplay
            key={`info-screen-${currentQuestion.id || currentQuestionIndex}`}
            content={currentQuestion.text}
            description={currentQuestion.description}
            displayTimeSeconds={currentQuestion.display_time_seconds}
            mediaUrls={orderedQuestionMediaUrls}
            onContinue={() => {
              // Auto-submit with "viewed" response and move to next question.
              // Pass 'viewed' explicitly so the submitted payload doesn't depend on the
              // async currentResponse state update having committed (which captured a stale '').
              handleResponseChange('viewed');
              handleSubmitResponse('viewed');
            }}
            disabled={isSubmitting}
          />
        );

      case 'percentage_allocation':
        const allocOptions = currentQuestion.allocation_options || currentQuestion.choices || [];
        const allocTarget = currentQuestion.allocation_target || 100;
        const allocResponse = (currentResponse as Record<string, number>) || {};
        const autoFillLast = currentQuestion.auto_fill_last !== false; // Default true
        const allowZero = currentQuestion.allow_zero !== false; // Default true

        // Calculate current total
        const allocTotal = Object.values(allocResponse).reduce((sum, val) => sum + (val || 0), 0);

        // Get status color
        const getAllocStatusColor = () => {
          if (allocTotal > allocTarget) return 'text-red-600';
          if (allocTotal < allocTarget) return 'text-amber-600';
          return 'text-emerald-600';
        };

        const getAllocStatusBg = () => {
          if (allocTotal > allocTarget) return 'bg-red-50 border-red-200';
          if (allocTotal < allocTarget) return 'bg-amber-50 border-amber-200';
          return 'bg-emerald-50 border-emerald-200';
        };

        return (
          <div className="space-y-4">
            {renderSimpleQuestionMedia()}
            <div className="text-sm text-gray-600 mb-4">
              Allocate values across the options below. {autoFillLast ? 'The last option will be auto-calculated.' : `Total must equal ${allocTarget}.`}
            </div>

            <div className="space-y-3">
              {allocOptions.map((option: string, index: number) => {
                const isLastOption = autoFillLast && index === allocOptions.length - 1;
                const otherOptionsTotal = allocOptions
                  .slice(0, -1)
                  .reduce((sum: number, opt: string) => sum + (allocResponse[opt] || 0), 0);
                const autoValue = isLastOption ? Math.max(0, allocTarget - otherOptionsTotal) : null;
                const currentValue = isLastOption && autoFillLast ? autoValue : (allocResponse[option] || 0);

                return (
                  <div key={index} className="flex items-center justify-between gap-4 p-3 bg-white border border-gray-200 rounded-lg">
                    <span className="flex-1 text-gray-900 font-medium">{option}</span>
                    <div className="flex items-center gap-2">
                      {isLastOption ? (
                        <div className="flex items-center gap-2">
                          <span className={`w-20 text-right font-semibold tabular-nums ${autoValue !== null && autoValue < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                            {autoValue !== null ? autoValue : 0}
                          </span>
                          <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">Auto</span>
                        </div>
                      ) : (
                        <>
                          <input
                            type="number"
                            min={allowZero ? 0 : 1}
                            max={allocTarget}
                            step={1}
                            value={currentValue || ''}
                            onChange={(e) => {
                              let val = parseInt(e.target.value) || 0;
                              // Enforce minimum of 1 when allow_zero is false
                              if (!allowZero && val < 1 && e.target.value !== '') {
                                val = 1;
                              }
                              // Initialize all options with 0 first, then spread existing values
                              const initializedResponse = allocOptions.reduce((acc: Record<string, number>, opt: string) => {
                                acc[opt] = allocResponse[opt] || 0;
                                return acc;
                              }, {} as Record<string, number>);
                              const newResponse = { ...initializedResponse, [option]: val };

                              // If auto_fill_last, calculate the last option's value
                              if (autoFillLast && allocOptions.length > 0) {
                                const lastOption = allocOptions[allocOptions.length - 1];
                                const otherTotal = allocOptions
                                  .slice(0, -1)
                                  .reduce((sum: number, opt: string) => {
                                    if (opt === option) return sum + val;
                                    return sum + (initializedResponse[opt] || 0);
                                  }, 0);
                                newResponse[lastOption] = Math.max(0, allocTarget - otherTotal);
                              }

                              handleResponseChange(newResponse);
                            }}
                            className="w-20 px-3 py-2 text-right border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent tabular-nums"
                            placeholder={allowZero ? "0" : "1"}
                          />
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Total indicator */}
            <div className={`flex items-center justify-between p-3 rounded-lg border ${getAllocStatusBg()}`}>
              <span className="font-medium text-gray-700">Total</span>
              <div className="flex items-center gap-2">
                <span className={`font-bold text-lg tabular-nums ${getAllocStatusColor()}`}>
                  {autoFillLast
                    ? allocOptions.slice(0, -1).reduce((sum: number, opt: string) => sum + (allocResponse[opt] || 0), 0) +
                      Math.max(0, allocTarget - allocOptions.slice(0, -1).reduce((sum: number, opt: string) => sum + (allocResponse[opt] || 0), 0))
                    : allocTotal
                  }
                </span>
                <span className="text-gray-500">/ {allocTarget}</span>
                {allocTotal === allocTarget && (
                  <svg className="w-5 h-5 text-emerald-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
            </div>

            {/* Warning messages */}
            {allocTotal > allocTarget && (
              <p className="text-sm text-red-600">
                Total exceeds {allocTarget}. Please reduce some values.
              </p>
            )}
          </div>
        );

      case 'photo_upload':
        return (
          <PhotoUploadQuestion
          // Keyed so a new question REMOUNTS it. Same-type questions would otherwise reconcile
          // in place, keeping the previous question's images and its still-running poll alive.
          key={`photo-${currentQuestion?.id ?? currentQuestionIndex}`}
            questionId={currentQuestion.id}
            surveyId={survey.id}
            sessionId={session?.id || 0}
            questionText={currentQuestion.text}
            maxImages={currentQuestion.max_images || 3}
            onComplete={(response) => {
              handleResponseChange(response);
            }}
            disabled={isSubmitting}
          />
        );

      default:
        return null;
    }
  };

  return (
    <>
      <style>{`
        @keyframes gentleBounce {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-4px); }
        }
        @keyframes waveform {
          0%, 100% { 
            transform: scaleY(0.3);
            opacity: 0.6;
          }
          50% { 
            transform: scaleY(1);
            opacity: 1;
          }
        }
      `}</style>
      {/* AI redundancy-skip acknowledgement (auto-dismisses) */}
      {aiSkipNotice && aiSkipNotice.count > 0 && (
        <div
          role="status"
          aria-live="polite"
          className="fixed top-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-4 py-2 rounded-lg bg-[#E8FCFA] border border-[#07E5D1] text-[#1C1C1E] shadow-sm text-sm font-medium"
        >
          <Check className="w-4 h-4 text-[#06D4C2]" />
          {`We skipped ${aiSkipNotice.count} question${aiSkipNotice.count === 1 ? '' : 's'} you already answered.`}
        </div>
      )}
      <div className="min-h-screen bg-gray-50 py-8">
      <div className={`${currentQuestion?.type === 'image_hotspot' ? 'max-w-6xl' : 'max-w-4xl'} mx-auto px-4 transition-all duration-300`}>
        <div className="bg-white rounded-xl shadow-sm">
          {/* Preview Mode Banner */}
          {isPreviewMode && (
            <div className="px-6 py-2 bg-amber-50 border-b border-amber-200 text-center">
              <span className="text-xs font-medium text-amber-700">Preview Mode — responses are not saved</span>
            </div>
          )}

          {/* Progress Header - hidden for screener questions */}
          {!currentQuestion?.is_screener && (
          <div className="px-6 pt-6 pb-4 border-b border-gray-200">
            <div className="flex items-center justify-end mb-4">
              <div className="text-sm font-medium text-gray-500">
                {progress.percent}% Complete
              </div>
            </div>

            {/* Progress Bar — percentage of the questions this respondent will actually be asked
                (estimated from conditional logic), so a branching survey never shows a scary raw total. */}
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress.percent}%` }}
              />
            </div>
          </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Question Content */}
          <div className="px-6 py-8">
            {(!isAnalyzing && !isCheckingSkip && !showThinking) ? (
              <>
                <div className="mb-8">

                  {/* Hide question text header for info_screen since InfoScreenDisplay shows it */}
                  {currentQuestion.type !== 'info_screen' && (
                    <QuestionText className="text-xl font-semibold text-gray-900 mb-6">
                      {followupQuestion || adaptedQuestions[currentQuestion.id] || currentQuestion.text}
                    </QuestionText>
                  )}
                  
                  {renderQuestionInput()}
                </div>

                {/* Navigation Buttons - hidden for info_screen since it has its own Continue button */}
                {currentQuestion.type !== 'info_screen' && (
                  <div className="flex justify-between">
                    {isPreviewMode ? (
                      <button
                        type="button"
                        onClick={handlePreviewSkip}
                        className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                      >
                        Skip
                      </button>
                    ) : <div />}
                    <Button
                      variant="primary"
                      onClick={() => handleSubmitResponse()}
                      // Also gated on the stimulus: single_choice submits from the option button,
                      // but every other type (multiple_choice, rating, ranking, matrix, text...)
                      // submits from HERE, outside the option fieldset. Without this, a respondent
                      // carrying a non-empty currentResponse — e.g. a restored/persisted answer —
                      // could advance past a question whose image had not painted yet.
                      disabled={!canProceed || isSubmitting || isAnalyzing || !stimulusReady}
                      icon={followupQuestion ? <ArrowRight className="w-4 h-4" /> : isLastQuestion ? <Check className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
                    >
                      {isSubmitting
                        ? UI_TEXT.AI_SURVEY.SESSION.BUTTONS.SUBMITTING 
                        : followupQuestion
                          ? UI_TEXT.BUTTONS.CONTINUE
                          : UI_TEXT.AI_SURVEY.SESSION.BUTTONS.NEXT
                      }
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <div className="flex items-center justify-center py-20">
                <div className="text-center">
                  <div className={`w-24 h-24 border-4 ${isCheckingSkip ? 'border-[#07E5D1]' : 'border-blue-500'} border-t-transparent rounded-full animate-spin mx-auto mb-6`} />
                  <p className="text-lg font-medium text-gray-900">
                    {isCheckingSkip ? 'Checking your answers…' : isAnalyzing ? analyzingMessage : thinkingMessage}
                  </p>
                  {showThinking && !isAnalyzing && !isCheckingSkip && (
                    <p className="mt-2 text-sm text-gray-500">Preparing your next question…</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default SurveySession;